import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Calendar, Clock, User, MapPin, Edit, Trash2, X, CheckCircle, List, Filter, XCircle, Search, FileText, Activity, Printer } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateJob, deleteJob, getJobs, refreshJobs, updateJobInArray, removeJobById } from '../../redux/features/jobSlice';
import { getWorkers } from '../../redux/features/workerSlice';
import { getPartners } from '../../redux/features/partnerSlice';
import { toast } from 'react-toastify';
import JobForm from '../forms/JobForm';
import axios from 'axios';
import TimeTracker from '../worker/TimeTracker';
import JobStatusControls from '../worker/JobStatusControls';
import JobMaterialsPanel from '../inventory/JobMaterialsPanel';
import WithdrawnItemsReminderBanner from '../inventory/WithdrawnItemsReminderBanner';
import { getOccupiedTimeSlots, formatTimeSlot } from '../../utils/timeSlotUtils';
import { API_BASE_URL } from '../../config/api.js';
import { getWorkerName, getWorkerColor, getWorkerInitials } from '../../utils/workerUtils';
import DatePicker from 'react-datepicker';
import DatePickerField from '../forms/job/DatePickerField';
import TimePickerField from '../forms/job/TimePickerField';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { getBusinessType } from '../../utils/businessTypeUtils';
import { computeCalendarRange } from '../../utils/calendarRangeUtils';
import { formatDisplayValue, getJobDisplayStatus } from '../../utils/displayUtils';
import { formatWorkOrderNumber } from '../../utils/workOrderPrintUtils';
import { fetchDeviceCategories } from '../../redux/api';
import DeadlineMarker from '../job/DeadlineMarker';
import WorkOrderPrintModal from '../jobs/WorkOrderPrintModal';
import { shouldApplyJobSocketPayload, getCurrentCompanyId } from '../../utils/tenantUtils';
import { isOutgoingHandoffJob } from '../../utils/handoffWorkflowUtils';
import {
  canUserCreateJobs,
  canUserDeleteJobs,
  getWorkerPermissionsFromStorage
} from '../../utils/profileRoleUtils';

// Helper function to parse date string as local date (avoiding timezone shift)
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  
  // Extract date parts from ISO string
  const dateOnly = dateString.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  
  // Create date in local timezone
  return new Date(year, month - 1, day);
};

const WORKING_HOUR_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2);
  const minute = index % 2 === 0 ? '00' : '30';
  const value = `${hour.toString().padStart(2, '0')}:${minute}`;
  return { value, label: value };
});

// Defensive helper: always return a real array, even if input is nullish or not iterable
const toArraySafe = (value) => {
  if (Array.isArray(value)) return [...value];
  if (value == null) return [];
  try {
    return Array.from(value);
  } catch (e) {
    console.warn('CalendarWithDragDrop: expected iterable, received', value);
    return [];
  }
};

const isEndAfterStart = (start, end) => {
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);
  const startTotalMinutes = startHour * 60 + startMinute;
  const endTotalMinutes = endHour * 60 + endMinute;
  return endTotalMinutes > startTotalMinutes;
};

const CalendarDateButton = React.forwardRef(function CalendarDateButton(
  { onClick, displayTitle, value, ...rest },
  ref
) {
  return (
    <button
      type="button"
      ref={ref}
      onClick={onClick}
      {...rest}
      className="text-xs sm:text-sm font-semibold text-white px-2.5 py-1 min-w-[128px] rounded-md hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-1.5"
      title="Izaberi datum"
    >
      <Calendar className="h-3.5 w-3.5 text-sky-400 flex-shrink-0" />
      <span className="truncate max-w-[180px] sm:max-w-none">{displayTitle || value}</span>
    </button>
  );
});

const CalendarWithDragDrop = ({
  jobs = [],
  onJobSchedule,
  onJobMove,
  onViewModeChange,
  onJobSelect,
  isNested = false,
  /** Uvek false u UI; zadržano radi kompatibilnosti / starog HMR. */
  loadAllJobs = false,
  /** 'company' = admin kalendar; 'worker' = samo dodeljeni poslovi, drugačiji filteri */
  schedulerAudience = 'company',
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isWorkerScheduler = schedulerAudience === 'worker';
  const { user, userType } = useSelector((state) => state.auth);
  const { workers } = useSelector((state) => state.worker || { workers: [] });
  const { partners } = useSelector((state) => state.partner || { partners: [] });
  const { socket, isConnected, emitJobStatusUpdate } = useWebSocket();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeSlots, setTimeSlots] = useState({});
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'week';
    return window.innerWidth < 768 ? 'day' : 'week';
  }); // 'day', 'week', 'month'
  const [selectedJob, setSelectedJob] = useState(null); // For job details modal
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const selectedJobWsRef = useRef(null);
  useEffect(() => {
    selectedJobWsRef.current = selectedJob;
  }, [selectedJob]);
  const [editingJob, setEditingJob] = useState(null); // For job edit modal
  const [showJobForm, setShowJobForm] = useState(false); // For job form modal
  const [editingJobId, setEditingJobId] = useState(null); // For inline date/time editing
  const [editingJobDateTime, setEditingJobDateTime] = useState({ date: '', time: '' }); // For inline editing
  const [workingHours, setWorkingHours] = useState({ start: '08:00', end: '20:00' });
  const [showDeviceImagesModal, setShowDeviceImagesModal] = useState(false);
  const selectedJobIdRef = useRef(null); // Track selected job ID to prevent accordion reset on timer updates
  const dateInputRef = useRef(null); // For date picker
  const jobDetailsInitialSections = {
    clientInfo: false,
    serviceDetails: false,
    timeSegments: false,
    issueDescription: false,
    inventoryMaterials: false,
    statusControls: false
  };
  const [jobDetailsOpenSections, setJobDetailsOpenSections] = useState(jobDetailsInitialSections);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });
  
  // Filter states - now arrays to support multiple selections
  const [filters, setFilters] = useState({
    worker: [],
    partner: [],
    deviceCategory: [],
    priority: [],
    warrantyOnly: false,
    inServiceOnly: false,
    /** Samo za radnika: 'OnSite' | 'InWorkshop' */
    serviceLocation: [],
  });
  const [deviceCategories, setDeviceCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedJobId, setHighlightedJobId] = useState(null);

  // Efektivan korisnik (Redux + fallback na localStorage) – bitno za radnički login
  const effectiveUser = useMemo(() => {
    if (user && user.result) return user;
    if (typeof window !== 'undefined') {
      try {
        const profileStr = localStorage.getItem('profile');
        if (profileStr) {
          const profile = JSON.parse(profileStr);
          return profile;
        }
      } catch (e) {
        console.error('CalendarWithDragDrop: error reading profile from localStorage', e);
      }
    }
    return user;
  }, [user]);

  const currentCompanyId = useMemo(() => getCurrentCompanyId(), []);

  const effectiveUserType = useMemo(() => {
    // First check localStorage (source of truth, especially when switching users)
    if (typeof window !== 'undefined') {
      const storedUserType = localStorage.getItem('userType');
      if (storedUserType) {
        return storedUserType;
      }
    }
    
    // Fallback to Redux userType
    if (userType) {
      return userType;
    }
    
    // Last resort: check user structure to determine type
    // Worker has firstName/lastName but no companyName
    // Company has companyName
    const userResult = effectiveUser?.result;
    if (userResult) {
      const hasWorkerStructure = userResult.firstName && userResult.lastName && !userResult.companyName;
      const hasCompanyStructure = userResult.companyName;
      
      if (hasWorkerStructure && !hasCompanyStructure) {
        return 'worker';
      }
      if (hasCompanyStructure) {
        return 'company';
      }
    }
    
    return 'company';
  }, [userType, effectiveUser]);

  const workerPermissions = useMemo(() => getWorkerPermissionsFromStorage(), []);
  const canEditJobs = canUserCreateJobs(effectiveUserType, workerPermissions);
  const canDeleteJobs = canUserDeleteJobs(effectiveUserType, workerPermissions);

  // Helper: čovek-readable lokacija radnika na osnovu statusa posla
  const getWorkerLocationLabel = (job) => {
    if (!job) return '-';
    const status = job.status || '';

    if (status === 'On Road') return 'U putu ka klijentu';
    if (status === 'At Client') return 'Kod klijenta';
    if (status === 'In Repair' || status === 'In Progress') return 'U servisu / na poslu';
    if (status === 'Diagnosing') return 'Dijagnostikuje kvar';
    if (status === 'Completed' || status === 'Finished' || status === 'Archived') return 'Posao završen';
    if (status === 'In Pending') return 'Na čekanju';

    return status || '-';
  };

  useEffect(() => {
    dispatch(getWorkers());
    // Silently handle partner fetch errors - endpoint may not exist
    dispatch(getPartners()).catch(() => {
      // Partner endpoint may not be available, fail silently
    });
    
    const loadDeviceCategories = async () => {
      try {
        const response = await fetchDeviceCategories();
        if (response?.data) {
          setDeviceCategories(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Error fetching device categories:', error);
        setDeviceCategories([]);
      }
    };
    loadDeviceCategories();
  }, [dispatch]);

  // WebSocket listener for real-time job updates
  useEffect(() => {
    if (socket && isConnected) {
      socket.on('job_updated', (data) => {
        if (!shouldApplyJobSocketPayload(data)) return;

        const sel = selectedJobWsRef.current;
        // Update selectedJob if it's the one that was updated and includes timer data
        if (sel && data.jobId === sel._id) {
          // If job is archived or finished, close the modal
          if (data.status === 'Archived' || data.status === 'Finished' || data.status === 'Completed') {
            console.log('🎯 Calendar: Job completed/finished, closing modal');
            dispatch(updateJobInArray({
              jobId: data.jobId,
              updates: { status: data.status },
            }));
            setSelectedJob(null);
            return;
          }
          
          if (data.travelStartTime !== undefined || data.workStartTime !== undefined || 
              data.accumulatedTravelTime !== undefined || data.accumulatedWorkTime !== undefined ||
              data.status !== undefined) {
            // OPTIMIZED: Update job directly in Redux store without full refresh
            const updates = {};
            if ('travelStartTime' in data) updates.travelStartTime = data.travelStartTime;
            if ('workStartTime' in data) updates.workStartTime = data.workStartTime;
            if ('accumulatedTravelTime' in data) updates.accumulatedTravelTime = data.accumulatedTravelTime ?? 0;
            if ('accumulatedWorkTime' in data) updates.accumulatedWorkTime = data.accumulatedWorkTime ?? 0;
            if (data.status !== undefined) updates.status = data.status;
            
            if (Object.keys(updates).length > 0) {
              // Update in Redux store immediately (no full refresh)
              dispatch(updateJobInArray({
                jobId: data.jobId,
                updates
              }));
              
              // Also update selectedJob if modal is open
              setSelectedJob(prev => ({
                ...prev,
                ...updates
              }));
            }
            
            // Don't refresh jobs if modal is open - we update selectedJob directly
            // This prevents closing the modal when admin starts timers
            // NO FULL REFRESH - We already updated job in Redux store via updateJobInArray
            return; // Exit early to prevent immediate job refresh
          }
        }
        
        // For non-timer updates, update job in Redux store without full refresh
        if (data.job && data.jobId) {
          dispatch(updateJobInArray({
            jobId: data.jobId,
            updates: data.job,
            fullJob: data.job
          }));
        } else {
          const updates = {};
          if (data.status !== undefined) updates.status = data.status;

          if (Object.keys(updates).length > 0) {
            dispatch(updateJobInArray({
              jobId: data.jobId,
              updates
            }));
          }
        }
        
        if (data.status === 'Finished' || data.status === 'Completed' || data.status === 'Archived') {
          const selDel = selectedJobWsRef.current;
          if (selDel && data.jobId === selDel._id) {
            setSelectedJob(null);
          }
        }
      });

      socket.on('job_deleted', (data) => {
        console.log('📋 Calendar: Job deleted via WebSocket:', data);
        if (data?.jobId) dispatch(removeJobById(data.jobId));
        const selDel2 = selectedJobWsRef.current;
        if (selDel2 && data.jobId === selDel2._id) {
          setSelectedJob(null);
        }
      });

      socket.on('worker_status_changed', (data) => {
        console.log('👷 Calendar: Worker status changed:', data);
        // NO FULL REFRESH - Worker status change doesn't require job refresh
        // Jobs are already updated via WebSocket job_updated events
      });

      return () => {
        socket.off('job_updated');
        socket.off('job_deleted');
        socket.off('worker_status_changed');
      };
    }
  }, [socket, isConnected, dispatch]);
  
  // Sync selectedJob with latest job data from jobs array when jobs update
  useEffect(() => {
    // Sync selectedJob with latest job data from jobs array
    if (selectedJob && jobs.length > 0) {
      const latestJob = jobs.find(j => j._id === selectedJob._id);
      if (latestJob) {
        // Check if timer data, status or report (e.g. potpis) changed
        const hasChanges = 
          latestJob.travelStartTime !== selectedJob.travelStartTime ||
          latestJob.workStartTime !== selectedJob.workStartTime ||
          (latestJob.accumulatedTravelTime ?? 0) !== (selectedJob.accumulatedTravelTime ?? 0) ||
          (latestJob.accumulatedWorkTime ?? 0) !== (selectedJob.accumulatedWorkTime ?? 0) ||
          latestJob.status !== selectedJob.status ||
          (latestJob.report?.customerSignature || '') !== (selectedJob.report?.customerSignature || '');
        
        if (hasChanges) {
          console.log('🔄 Calendar: Syncing selectedJob with jobs array', {
            jobId: latestJob._id,
            travelStartTime: latestJob.travelStartTime,
            workStartTime: latestJob.workStartTime,
            status: latestJob.status
          });
          setSelectedJob(latestJob);
        }
      }
    }
  }, [jobs, selectedJob?._id]);
  
  // Real-time timer update effect - refresh timer display every second ONLY when timers are active
  useEffect(() => {
    if (!selectedJob) {
      return;
    }
    
    // Check if timer is actually active (not just that startTime exists, but status matches)
    const hasActiveTravel = selectedJob.travelStartTime !== null && 
                           selectedJob.travelStartTime !== undefined && 
                           selectedJob.status === 'On Road';
    const hasActiveWork = selectedJob.workStartTime !== null && 
                         selectedJob.workStartTime !== undefined && 
                         (selectedJob.status === 'In Repair' || selectedJob.status === 'In Progress');
    
    if (!hasActiveTravel && !hasActiveWork) {
      return; // No active timers, don't update
    }
    
    const interval = setInterval(() => {
      // Force re-render to update timer display ONLY when timers are active
      setSelectedJob(prev => {
        if (!prev) return prev;
        // Only update if timer is still active
        const stillActiveTravel = prev.travelStartTime !== null && 
                                 prev.travelStartTime !== undefined && 
                                 prev.status === 'On Road';
        const stillActiveWork = prev.workStartTime !== null && 
                               prev.workStartTime !== undefined && 
                               (prev.status === 'In Repair' || prev.status === 'In Progress');
        if (stillActiveTravel || stillActiveWork) {
          return { ...prev };
        }
        return prev;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [selectedJob]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Force day view on mobile for better readability
  useEffect(() => {
    if (isMobile && viewMode !== 'day') {
      setViewMode('day');
    }
  }, [isMobile, viewMode]);

  // Notify parent component when view mode changes
  useEffect(() => {
    if (onViewModeChange) {
      onViewModeChange(viewMode);
    }
  }, [viewMode, onViewModeChange]);

  // Učitaj poslove za vidljivi period (dan / nedelja / mesec) + nezakazani; loadAllJobs samo ako eksplicitno true
  useEffect(() => {
    const businessType = getBusinessType();
    if (!businessType) return;
    if (loadAllJobs) {
      dispatch(getJobs({ businessType, fetchAll: true }));
      return;
    }
    const { startDate, endDate } = computeCalendarRange(currentDate, viewMode);
    dispatch(
      getJobs({
        businessType,
        startDate,
        endDate,
        includeUnscheduled: true,
      })
    );
  }, [currentDate, viewMode, loadAllJobs, dispatch]);

  // Generate time slots for each day
  const generateTimeSlots = (date) => {
    const slots = [];
    const [startHour, startMinute] = workingHours.start.split(':').map(Number);
    const [endHour, endMinute] = workingHours.end.split(':').map(Number);
    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;

    if (endTotalMinutes <= startTotalMinutes) {
      return slots;
    }

    for (let minutes = startTotalMinutes; minutes < endTotalMinutes; minutes += 30) {
      const hour = Math.floor(minutes / 60);
      const minute = minutes % 60;
        slots.push({
          id: `${date.toISOString().split('T')[0]}-${hour}-${minute}`,
          time: `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
          date: date.toISOString().split('T')[0],
          hour,
          minute
        });
    }
    return slots;
  };

  // Filter jobs based on filters and search query
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      // Exclude Archived, Completed, and Cancelled jobs - Finished jobs stay visible until archived
      if (job.status === 'Archived' || job.status === 'Completed' || job.status === 'Cancelled') return false;

      // Prosleđeni poslovi (pošiljalac) ne ostaju u kalendaru — vide se u „Prosleđeni poslovi”
      if (currentCompanyId && isOutgoingHandoffJob(job, currentCompanyId)) return false;
      
      // Only show scheduled jobs (with serviceDate)
      if (!job.serviceDate) return false;
      
      // Search filter - check if search query matches client fields only
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const clientName = (job.clientName || '').toLowerCase();
        const clientAddress = (job.clientAddress || '').toLowerCase();
        const clientEmail = (job.clientEmail || '').toLowerCase();
        const clientPhone = (job.clientPhone || '').toLowerCase();
        
        const matchesSearch = 
          clientName.includes(query) ||
          clientAddress.includes(query) ||
          clientEmail.includes(query) ||
          clientPhone.includes(query);
        
        if (!matchesSearch) return false;
      }
      
      // Worker filter - check if job matches any selected worker
      const matchesWorker = (() => {
        if (isWorkerScheduler) return true;
        // If no worker filter is selected, match all jobs
        if (!filters.worker || (Array.isArray(filters.worker) && filters.worker.length === 0) || (!Array.isArray(filters.worker) && !filters.worker)) {
          return true;
        }
        
        if (!job.assignedTo || job.assignedTo === 'Unassigned' || job.assignedTo === '') return false;
        
        // Convert to array if it's a string (backward compatibility)
        const filterWorkerIds = Array.isArray(filters.worker) ? filters.worker : [filters.worker].filter(Boolean);
        if (filterWorkerIds.length === 0) return true; // No filter selected
        
        // Check if job matches any of the selected workers
        return filterWorkerIds.some(filterWorkerId => {
          const workerId = String(filterWorkerId).trim();
          if (!workerId) return false;
          
          // Handle populated object
          if (typeof job.assignedTo === 'object' && job.assignedTo !== null) {
            const assignedToId = String(job.assignedTo._id || job.assignedTo.id || '').trim();
            if (assignedToId && assignedToId === workerId) return true;
            
            // Also check if it's a partner object (has companyName or name)
            if (job.assignedTo.companyName || job.assignedTo.name) {
              const selectedPartnerObj = partners.find(p => {
                const partnerId = String(p._id || p.id || '').trim();
                return partnerId === workerId;
              });
              if (selectedPartnerObj) {
                const selectedPartnerId = String(selectedPartnerObj._id || selectedPartnerObj.id || '').trim();
                if (selectedPartnerId === assignedToId) return true;
              }
            }
          }
          
          // Handle string ID (with or without prefix)
          const assignedToStr = String(job.assignedTo).trim();
          if (!assignedToStr || assignedToStr === 'Unassigned') return false;
          
          // Direct ID match (most common case - server stores as plain ID)
          if (assignedToStr === workerId) return true;
          
          // Check if assignedTo has worker: prefix
          if (assignedToStr.startsWith('worker:')) {
            const extractedWorkerId = assignedToStr.replace('worker:', '').trim();
            if (extractedWorkerId === workerId) return true;
          }
          
          // Check if it's a worker name match (legacy support)
          const selectedWorkerObj = workers.find(w => {
            const wId = String(w._id || w.id || '').trim();
            return wId === workerId;
          });
          if (selectedWorkerObj) {
            const workerName = `${selectedWorkerObj.firstName || ''} ${selectedWorkerObj.lastName || ''}`.trim();
            if (assignedToStr === workerName || 
                assignedToStr === selectedWorkerObj.firstName || 
                assignedToStr === selectedWorkerObj.lastName) {
              return true;
            }
          }
          
          return false;
        });
      })();

      // Partner filter - check if job matches any selected service partners
      const matchesPartner = (() => {
        if (isWorkerScheduler) return true;
        if (!filters.partner || (Array.isArray(filters.partner) && filters.partner.length === 0) || (!Array.isArray(filters.partner) && !filters.partner)) {
          return true;
        }

        if (!job.assignedTo || job.assignedTo === 'Unassigned' || job.assignedTo === '') return false;

        const filterPartnerIds = Array.isArray(filters.partner) ? filters.partner : [filters.partner].filter(Boolean);
        if (filterPartnerIds.length === 0) return true;

        return filterPartnerIds.some((filterPartnerId) => {
          const partnerId = String(filterPartnerId).trim();
          if (!partnerId) return false;

          if (typeof job.assignedTo === 'object' && job.assignedTo !== null) {
            const assignedToId = String(job.assignedTo._id || job.assignedTo.id || '').trim();
            if (assignedToId && assignedToId === partnerId) return true;
          }

          const assignedToStr = String(job.assignedTo).trim();
          if (!assignedToStr || assignedToStr === 'Unassigned') return false;
          if (assignedToStr === partnerId) return true;
          if (assignedToStr.startsWith('partner:')) {
            const extractedPartnerId = assignedToStr.replace('partner:', '').trim();
            if (extractedPartnerId === partnerId) return true;
          }

          const selectedPartnerObj = partners.find((p) => String(p._id || p.id || '').trim() === partnerId);
          if (selectedPartnerObj) {
            const partnerName = (selectedPartnerObj.companyName || selectedPartnerObj.name || '').trim();
            if (partnerName && (assignedToStr === partnerName || assignedToStr === selectedPartnerObj.contactPerson)) {
              return true;
            }
          }

          return false;
        });
      })();
      
      // Device category filter - check if job matches any selected category
      const matchesDeviceCategory = (() => {
        // If no device category filter is selected, match all jobs
        if (!filters.deviceCategory || (Array.isArray(filters.deviceCategory) && filters.deviceCategory.length === 0) || (!Array.isArray(filters.deviceCategory) && !filters.deviceCategory)) {
          return true;
        }
        
        // Convert to array if it's a string (backward compatibility)
        const filterCategoryIds = Array.isArray(filters.deviceCategory) ? filters.deviceCategory : [filters.deviceCategory].filter(Boolean);
        if (filterCategoryIds.length === 0) return true; // No filter selected
        
        // Check if job matches any of the selected categories
        return filterCategoryIds.some(filterCategoryId => {
          if (job.deviceCategoryId) {
            const jobCategoryId = job.deviceCategoryId.toString();
            const filterId = filterCategoryId.toString();
            if (jobCategoryId === filterId) return true;
          }
          const selectedCategory = deviceCategories.find(cat => 
            (cat.id?.toString() === filterCategoryId.toString()) || 
            (cat._id?.toString() === filterCategoryId.toString())
          );
          if (selectedCategory && job.deviceCategoryName === selectedCategory.name) return true;
          return false;
        });
      })();
      
      // Priority filter - check if job matches any selected priority
      const matchesPriority = (() => {
        // If no priority filter is selected, match all jobs
        if (!filters.priority || (Array.isArray(filters.priority) && filters.priority.length === 0) || (!Array.isArray(filters.priority) && !filters.priority)) {
          return true;
        }
        
        // Convert to array if it's a string (backward compatibility)
        const filterPriorities = Array.isArray(filters.priority) ? filters.priority : [filters.priority].filter(Boolean);
        if (filterPriorities.length === 0) return true; // No filter selected
        return filterPriorities.includes(job.priority);
      })();

      const matchesWarranty = !filters.warrantyOnly || job.hasWarranty === true;
      const matchesInService =
        isWorkerScheduler ||
        !filters.inServiceOnly ||
        job.serviceLocation === 'InWorkshop' ||
        ['In Repair', 'In Progress', 'Diagnosing', 'Waiting for Parts'].includes(job.status);

      const matchesServiceLocation = (() => {
        if (!isWorkerScheduler) return true;
        const locs = Array.isArray(filters.serviceLocation) ? filters.serviceLocation : [];
        if (locs.length === 0) return true;
        const jobLoc = job.serviceLocation === 'InWorkshop' ? 'InWorkshop' : 'OnSite';
        return locs.includes(jobLoc);
      })();
      
      // AND logic: show jobs that match ALL of the selected filters
      // If no filters are selected, show all jobs
      const hasWorkerFilter =
        !isWorkerScheduler &&
        filters.worker &&
        (Array.isArray(filters.worker) ? filters.worker.length > 0 : filters.worker);
      const hasPartnerFilter =
        !isWorkerScheduler &&
        filters.partner &&
        (Array.isArray(filters.partner) ? filters.partner.length > 0 : filters.partner);
      const hasDeviceCategoryFilter = filters.deviceCategory && (Array.isArray(filters.deviceCategory) ? filters.deviceCategory.length > 0 : filters.deviceCategory);
      const hasPriorityFilter = filters.priority && (Array.isArray(filters.priority) ? filters.priority.length > 0 : filters.priority);
      const hasWarrantyFilter = !!filters.warrantyOnly;
      const hasInServiceFilter = !isWorkerScheduler && !!filters.inServiceOnly;
      const hasServiceLocationFilter =
        isWorkerScheduler &&
        Array.isArray(filters.serviceLocation) &&
        filters.serviceLocation.length > 0;
      
      const hasAnyFilter =
        hasWorkerFilter ||
        hasPartnerFilter ||
        hasDeviceCategoryFilter ||
        hasPriorityFilter ||
        hasWarrantyFilter ||
        hasInServiceFilter ||
        hasServiceLocationFilter;
      
      if (!hasAnyFilter) {
        // No filters selected, show all jobs
        return true;
      }
      
      // At least one filter is selected, show jobs that match ALL selected filters
      // If a filter type is not selected, it doesn't need to match (matchesWorker/matchesDeviceCategory/matchesPriority return true)
      return (
        matchesWorker &&
        matchesPartner &&
        matchesDeviceCategory &&
        matchesPriority &&
        matchesWarranty &&
        matchesInService &&
        matchesServiceLocation
      );
    });
  }, [jobs, filters, deviceCategories, workers, partners, searchQuery, isWorkerScheduler, currentCompanyId]);

  // Helper function to check if a date is in current view
  const isDateInCurrentView = (date) => {
    if (!date) return false;
    const jobDate = parseLocalDate(date);
    if (!jobDate) return false;
    
    const current = new Date(currentDate);
    
    if (viewMode === 'day') {
      return jobDate.toDateString() === current.toDateString();
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(current);
      const dayOfWeek = current.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      startOfWeek.setDate(current.getDate() + diff);
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      
      return jobDate >= startOfWeek && jobDate <= endOfWeek;
    } else { // month
      return jobDate.getMonth() === current.getMonth() && 
             jobDate.getFullYear() === current.getFullYear();
    }
  };

  // Helper function to highlight matching text
  const highlightText = (text, query) => {
    if (!query || !text) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-300 text-black">{part}</mark>
      ) : part
    );
  };

  // Auto-navigate to first matching job when search query changes
  useEffect(() => {
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const firstMatchingJob = jobs.find(job => {
        if (job.status === 'Archived' || !job.serviceDate) return false;
        
        const clientName = (job.clientName || '').toLowerCase();
        const clientAddress = (job.clientAddress || '').toLowerCase();
        const clientEmail = (job.clientEmail || '').toLowerCase();
        const clientPhone = (job.clientPhone || '').toLowerCase();
        
        return clientName.includes(query) ||
               clientAddress.includes(query) ||
               clientEmail.includes(query) ||
               clientPhone.includes(query);
      });
      
      if (firstMatchingJob && firstMatchingJob.serviceDate) {
        const jobDate = parseLocalDate(firstMatchingJob.serviceDate);
        if (jobDate && !isDateInCurrentView(firstMatchingJob.serviceDate)) {
          // Navigate to the job's date
          setCurrentDate(jobDate);
          setHighlightedJobId(firstMatchingJob._id);
          
          // Clear highlight after 3 seconds
          setTimeout(() => setHighlightedJobId(null), 3000);
        } else if (jobDate) {
          setHighlightedJobId(firstMatchingJob._id);
          setTimeout(() => setHighlightedJobId(null), 3000);
        }
      }
    } else {
      setHighlightedJobId(null);
    }
  }, [searchQuery, jobs, currentDate, viewMode]);

  // Count jobs by each filter criteria
  const getFilterCounts = useMemo(() => {
    const counts = {
      worker: 0,
      partner: 0,
      deviceCategory: 0,
      priority: 0,
      warrantyOnly: 0,
      inServiceOnly: 0,
      serviceLocation: 0,
    };

    // Count by worker - sum of all selected workers
    const filterWorkerIds = Array.isArray(filters.worker) ? filters.worker : (filters.worker ? [filters.worker] : []);
    if (!isWorkerScheduler && filterWorkerIds.length > 0) {
      counts.worker = filterWorkerIds.reduce((total, filterWorkerId) => {
        const count = jobs.filter(job => {
          if (job.status === 'Archived' || job.status === 'Finished' || job.status === 'Completed' || job.status === 'Cancelled') return false;
          if (!job.serviceDate) return false;
          if (!job.assignedTo || job.assignedTo === 'Unassigned' || job.assignedTo === '') return false;
          
          const workerId = String(filterWorkerId).trim();
          if (!workerId) return false;
          
          if (typeof job.assignedTo === 'object' && job.assignedTo !== null) {
            const assignedToId = String(job.assignedTo._id || job.assignedTo.id || '').trim();
            if (assignedToId && assignedToId === workerId) return true;
            
            // Also check if it's a partner object
            if (job.assignedTo.companyName || job.assignedTo.name) {
              const selectedPartnerObj = partners.find(p => {
                const partnerId = String(p._id || p.id || '').trim();
                return partnerId === workerId;
              });
              if (selectedPartnerObj) {
                const selectedPartnerId = String(selectedPartnerObj._id || selectedPartnerObj.id || '').trim();
                if (selectedPartnerId === assignedToId) return true;
              }
            }
          }
          
          const assignedToStr = String(job.assignedTo).trim();
          if (!assignedToStr || assignedToStr === 'Unassigned') return false;
          
          // Direct ID match
          if (assignedToStr === workerId) return true;
          
          // Check if assignedTo has worker: prefix
          if (assignedToStr.startsWith('worker:')) {
            const extractedWorkerId = assignedToStr.replace('worker:', '').trim();
            if (extractedWorkerId === workerId) return true;
          }
          
          const selectedWorkerObj = workers.find(w => {
            const wId = String(w._id || w.id || '').trim();
            return wId === workerId;
          });
          if (selectedWorkerObj) {
            const workerName = `${selectedWorkerObj.firstName || ''} ${selectedWorkerObj.lastName || ''}`.trim();
            return assignedToStr === workerName || 
                   assignedToStr === selectedWorkerObj.firstName || 
                   assignedToStr === selectedWorkerObj.lastName;
          }
          
          return false;
        }).length;
        return total + count;
      }, 0);
    }

    // Count by partners
    const filterPartnerIds = Array.isArray(filters.partner) ? filters.partner : (filters.partner ? [filters.partner] : []);
    if (!isWorkerScheduler && filterPartnerIds.length > 0) {
      counts.partner = filterPartnerIds.reduce((total, filterPartnerId) => {
        const count = jobs.filter((job) => {
          if (job.status === 'Archived' || job.status === 'Finished' || job.status === 'Completed' || job.status === 'Cancelled') return false;
          if (!job.serviceDate) return false;
          if (!job.assignedTo || job.assignedTo === 'Unassigned' || job.assignedTo === '') return false;

          const partnerId = String(filterPartnerId).trim();
          if (!partnerId) return false;

          if (typeof job.assignedTo === 'object' && job.assignedTo !== null) {
            const assignedToId = String(job.assignedTo._id || job.assignedTo.id || '').trim();
            if (assignedToId && assignedToId === partnerId) return true;
          }

          const assignedToStr = String(job.assignedTo).trim();
          if (assignedToStr === partnerId) return true;
          if (assignedToStr.startsWith('partner:')) {
            const extractedPartnerId = assignedToStr.replace('partner:', '').trim();
            if (extractedPartnerId === partnerId) return true;
          }

          const selectedPartnerObj = partners.find((p) => String(p._id || p.id || '').trim() === partnerId);
          if (selectedPartnerObj) {
            const partnerName = (selectedPartnerObj.companyName || selectedPartnerObj.name || '').trim();
            if (partnerName && (assignedToStr === partnerName || assignedToStr === selectedPartnerObj.contactPerson)) {
              return true;
            }
          }

          return false;
        }).length;
        return total + count;
      }, 0);
    }

    // Count by device category - sum of all selected categories
    const filterCategoryIds = Array.isArray(filters.deviceCategory) ? filters.deviceCategory : (filters.deviceCategory ? [filters.deviceCategory] : []);
    if (filterCategoryIds.length > 0) {
      counts.deviceCategory = filterCategoryIds.reduce((total, filterCategoryId) => {
        const count = jobs.filter(job => {
          if (job.status === 'Archived' || job.status === 'Finished' || job.status === 'Completed' || job.status === 'Cancelled') return false;
          if (!job.serviceDate) return false;
          
          if (job.deviceCategoryId) {
            const jobCategoryId = job.deviceCategoryId.toString();
            const filterId = filterCategoryId.toString();
            if (jobCategoryId === filterId) return true;
          }
          const selectedCategory = deviceCategories.find(cat => 
            (cat.id?.toString() === filterCategoryId.toString()) || 
            (cat._id?.toString() === filterCategoryId.toString())
          );
          if (selectedCategory && job.deviceCategoryName === selectedCategory.name) return true;
          return false;
        }).length;
        return total + count;
      }, 0);
    }

    // Count by priority - sum of all selected priorities
    const filterPriorities = Array.isArray(filters.priority) ? filters.priority : (filters.priority ? [filters.priority] : []);
    if (filterPriorities.length > 0) {
      counts.priority = filterPriorities.reduce((total, filterPriority) => {
        const count = jobs.filter(job => {
          if (job.status === 'Archived' || job.status === 'Finished' || job.status === 'Completed' || job.status === 'Cancelled') return false;
          if (!job.serviceDate) return false;
          return job.priority === filterPriority;
        }).length;
        return total + count;
      }, 0);
    }

    if (filters.warrantyOnly) {
      counts.warrantyOnly = jobs.filter((job) => {
        if (job.status === 'Archived' || job.status === 'Finished' || job.status === 'Completed' || job.status === 'Cancelled') return false;
        if (!job.serviceDate) return false;
        return job.hasWarranty === true;
      }).length;
    }

    if (!isWorkerScheduler && filters.inServiceOnly) {
      counts.inServiceOnly = jobs.filter((job) => {
        if (job.status === 'Archived' || job.status === 'Finished' || job.status === 'Completed' || job.status === 'Cancelled') return false;
        if (!job.serviceDate) return false;
        return (
          job.serviceLocation === 'InWorkshop' ||
          ['In Repair', 'In Progress', 'Diagnosing', 'Waiting for Parts'].includes(job.status)
        );
      }).length;
    }

    const filterLocs = Array.isArray(filters.serviceLocation) ? filters.serviceLocation : [];
    if (isWorkerScheduler && filterLocs.length > 0) {
      counts.serviceLocation = filterLocs.reduce((total, loc) => {
        const count = jobs.filter((job) => {
          if (job.status === 'Archived' || job.status === 'Finished' || job.status === 'Completed' || job.status === 'Cancelled') return false;
          if (!job.serviceDate) return false;
          const jobLoc = job.serviceLocation === 'InWorkshop' ? 'InWorkshop' : 'OnSite';
          return jobLoc === loc;
        }).length;
        return total + count;
      }, 0);
    }

    return counts;
  }, [jobs, filters, deviceCategories, workers, partners, isWorkerScheduler]);

  // Get jobs for a specific date - sorted chronologically by time
  const getJobsForDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const dateFilteredJobs = filteredJobs.filter(job => {
      // Parse job date as local date
      const jobDate = parseLocalDate(job.serviceDate);
      if (!jobDate) return false;
      
      const jobYear = jobDate.getFullYear();
      const jobMonth = String(jobDate.getMonth() + 1).padStart(2, '0');
      const jobDay = String(jobDate.getDate()).padStart(2, '0');
      const jobDateStr = `${jobYear}-${jobMonth}-${jobDay}`;
      
      return jobDateStr === dateStr;
    });
    
    // Sort jobs by scheduled time (chronologically)
    return dateFilteredJobs.sort((a, b) => {
      const timeA = a.scheduledTime || '23:59';
      const timeB = b.scheduledTime || '23:59';
      // Ensure time format is HH:MM
      const normalizedTimeA = timeA.length >= 5 ? timeA.substring(0, 5) : timeA;
      const normalizedTimeB = timeB.length >= 5 ? timeB.substring(0, 5) : timeB;
      return normalizedTimeA.localeCompare(normalizedTimeB);
    });
  };

  // Helper: Convert time to minutes since midnight
  const timeToMinutes = (hour, minute) => {
    return hour * 60 + minute;
  };

const minutesToTimeString = (totalMinutes) => {
  const normalized = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hour = Math.floor(normalized / 60);
  const minute = normalized % 60;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

  // Get jobs for a specific time slot - checks if slot falls within job duration
  const getJobsForTimeSlot = (date, hour, minute) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    const slotStartMinutes = timeToMinutes(hour, minute);
    const slotEndMinutes = slotStartMinutes + 30; // Each slot is 30 minutes
    
    return filteredJobs.filter(job => {
      if (!job.serviceDate || !job.scheduledTime) return false;
      if (job.status === 'Archived') return false; // Exclude only archived jobs - finished jobs should remain visible
      
      // Parse job date as local date
      const jobDate = parseLocalDate(job.serviceDate);
      if (!jobDate) return false;
      
      const jobYear = jobDate.getFullYear();
      const jobMonth = String(jobDate.getMonth() + 1).padStart(2, '0');
      const jobDay = String(jobDate.getDate()).padStart(2, '0');
      const jobDateStr = `${jobYear}-${jobMonth}-${jobDay}`;
      
      // Check if same date
      if (jobDateStr !== dateStr) return false;
      
      // Get job start and end times
      const [jobHour, jobMinute] = job.scheduledTime.split(':').map(Number);
      const jobStartMinutes = timeToMinutes(jobHour, jobMinute);
      
      // If no duration, assume 1 hour
      const duration = job.estimatedDuration || 1;
      const jobEndMinutes = jobStartMinutes + (duration * 60);
      
      // Check if slot overlaps with job time range
      // Slot overlaps if: slot starts before job ends AND slot ends after job starts
      return slotStartMinutes < jobEndMinutes && slotEndMinutes > jobStartMinutes;
    });
  };

  // Arhiviranje posla direktno iz kalendara
  const handleArchiveJob = async (job) => {
    try {
      const token = effectiveUser?.token;
      if (!job?._id || !token) {
        toast.error('Nije moguće arhivirati posao (nedostaju podaci o korisniku ili poslu).');
        return;
      }

      await axios.patch(
        `${API_BASE_URL}/api/jobs/${job._id}`,
        { status: 'Archived' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Posao je arhiviran');

      // Emit WebSocket event for job archiving
      emitJobStatusUpdate({
        jobId: job._id,
        status: 'Archived',
        timestamp: new Date().toISOString()
      });

      dispatch(refreshJobs());
    } catch (error) {
      console.error('Error archiving job from calendar:', error);
      toast.error('Greška pri arhiviranju posla');
    }
  };

  const handleOpenJobForPrint = (job) => {
    setSelectedJob(job);
    setJobDetailsOpenSections(prev => ({
      ...jobDetailsInitialSections,
      statusControls: true
    }));
      if (onJobSelect) onJobSelect(job);
  };

  // Handle drag end
  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    // If dropped on a time slot
    if (destination.droppableId.startsWith('time-slot-')) {
      const [, , dateStr, hour, minute] = destination.droppableId.split('-');
      const scheduledDate = new Date(dateStr);
      const scheduledTime = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
      
      onJobSchedule(draggableId, scheduledDate, scheduledTime);
    }
  };

  // Handle job click - enable inline editing for date/time (not for Finished – they use Pregled)
  const handleJobClick = (job, e) => {
    e.stopPropagation();
    // Završeni poslovi: uvek otvori modal (pregled), nikad inline edit
    if (job.status === 'Finished') {
      handleOpenJobForPrint(job);
      return;
    }
    // Enable inline editing for date and time
    if (job.serviceDate && job.scheduledTime) {
      const dateStr = job.serviceDate.split('T')[0];
      const timeStr = job.scheduledTime.length >= 5 ? job.scheduledTime.substring(0, 5) : job.scheduledTime;
      setEditingJobId(job._id);
      setEditingJobDateTime({ date: dateStr, time: timeStr });
    } else {
      if (onJobSelect) {
        onJobSelect(job);
      } else {
        setSelectedJob(job);
      }
    }
  };

  const openJobDetails = (job, event) => {
    if (event) event.stopPropagation();
    if (onJobSelect) onJobSelect(job);
    else setSelectedJob(job);
  };

  const renderWorkerBadge = (job, workerColor, workerInitials, size = 'sm') => {
    if (!workerColor || !workerInitials) return null;
    const box = size === 'md' ? 'h-5 w-5 text-[9px]' : 'h-4 w-4 text-[8px]';
    return (
      <div
        className={`${box} rounded-full inline-flex items-center justify-center text-white font-semibold flex-shrink-0 overflow-hidden leading-none`}
        style={{ backgroundColor: workerColor }}
        title={getWorkerName(job.assignedTo, workers, partners)}
      >
        <span className="leading-none">{workerInitials}</span>
      </div>
    );
  };

  const renderJobListButton = (job) => (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (job.status === 'Finished') handleOpenJobForPrint(job);
        else openJobDetails(job, e);
      }}
      className="h-5 w-5 bg-blue-600 text-white rounded hover:bg-blue-700 inline-flex items-center justify-center flex-shrink-0"
      title={job.status === 'Finished' ? 'Pregled (štampa i arhiva)' : 'Detalji posla'}
    >
      <List className="h-3 w-3" />
    </button>
  );

  // Handle inline date/time save
  const handleInlineDateTimeSave = async (jobId) => {
    if (!editingJobDateTime.date || !editingJobDateTime.time) {
      toast.error('Datum i vreme su obavezni');
      return;
    }

    try {
      const job = jobs.find(j => j._id === jobId);
      if (!job) return;

      // Save date and time values before closing editing mode
      const dateToSave = editingJobDateTime.date;
      const timeToSave = editingJobDateTime.time;

      // Close editing mode immediately (before API call) to hide the box with icons
      setEditingJobId(null);
      setEditingJobDateTime({ date: '', time: '' });

      // Format date and time for API
      const dateTime = `${dateToSave}T${timeToSave}:00`;
      const scheduledTime = timeToSave;

      await dispatch(updateJob({
        id: jobId,
        jobData: {
          serviceDate: dateToSave,
          scheduledTime: scheduledTime,
          serviceDateTime: {
            date: dateToSave,
            time: scheduledTime
          }
        }
      })).unwrap();

      toast.success('Datum i vreme su uspešno ažurirani');
    } catch (error) {
      toast.error(error.message || 'Greška pri ažuriranju datuma i vremena');
      // Re-open editing mode if save failed
      const job = jobs.find(j => j._id === jobId);
      if (job) {
        setEditingJobId(jobId);
        const dateStr = job.serviceDate ? job.serviceDate.split('T')[0] : '';
        const timeStr = job.scheduledTime ? (job.scheduledTime.length >= 5 ? job.scheduledTime.substring(0, 5) : job.scheduledTime) : '';
        setEditingJobDateTime({ date: dateStr, time: timeStr });
      }
    }
  };

  // Handle inline date/time cancel
  const handleInlineDateTimeCancel = () => {
    setEditingJobId(null);
    setEditingJobDateTime({ date: '', time: '' });
  };

  // Handle job edit
  const handleEditJob = (job) => {
    setSelectedJob(null);
    setEditingJob(job);
  };

  // Handle job delete
  const handleDeleteJob = async (job) => {
    if (window.confirm(`Da li ste sigurni da želite obrisati posao za klijenta "${job.clientName}"?`)) {
      try {
        await dispatch(deleteJob(job._id)).unwrap();
        dispatch(refreshJobs());
        setSelectedJob(null);
        toast.success('Posao uspešno obrisan');
      } catch (error) {
        toast.error(error.message || 'Greška pri brisanju posla');
      }
    }
  };

  // Handle status action for workers
  const handleStatusAction = async (status, jobId, clientAddress) => {
    try {
      const token = user?.token;
      
      // Update worker status
      await axios.put(
        `${API_BASE_URL}/api/worker/${user.result._id}/status`,
        {
          status,
          currentJobId: jobId,
          location: clientAddress || 'Nepoznata adresa',
          notes: `Posao: ${jobId}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update job status
      const jobStatusMap = {
        'on_the_road': 'On Road',
        'at_client': 'In Progress',
        'completed': 'Completed'
      };
      
      const jobStatus = jobStatusMap[status];
      if (jobStatus) {
        await axios.put(
          `${API_BASE_URL}/api/jobs/${jobId}`,
          { status: jobStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ Job status updated to:', jobStatus);
      }
      
      const statusLabels = {
        'on_the_road': 'U putu ka stranci',
        'at_client': 'Kod stranke',
        'completed': 'Završeno'
      };
      
      toast.success(`Status ažuriran: ${statusLabels[status]}`);
      
      // Refresh jobs to show updated status
      dispatch(refreshJobs());
      
      setSelectedJob(null); // Close modal after action
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Greška pri ažuriranju statusa');
    }
  };

  // Generate calendar days based on view mode
  const generateCalendarDays = () => {
    if (viewMode === 'day') {
      // Just current day
      return [new Date(currentDate)];
    } else if (viewMode === 'week') {
      // Current week (7 days) - starting from Monday
      const startDate = new Date(currentDate);
      const dayOfWeek = currentDate.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days, else go to Monday
      startDate.setDate(currentDate.getDate() + diff);
      
      const days = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        days.push(date);
      }
      return days;
    } else {
      // Month view (full calendar grid - 35 or 42 days) - starting from Monday
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      const firstDayOfWeek = firstDay.getDay();
      const diff = firstDayOfWeek === 0 ? -6 : 1 - firstDayOfWeek;
      startDate.setDate(firstDay.getDate() + diff);
      
      const days = [];
      for (let i = 0; i < 35; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        days.push(date);
      }
      return days;
    }
  };

  const getJobTimeSegments = (job) => {
    if (!job || !job.scheduledTime) return [];
    const [startHour, startMinute] = job.scheduledTime.split(':').map(Number);
    if (Number.isNaN(startHour) || Number.isNaN(startMinute)) return [];
    const startMinutes = startHour * 60 + startMinute;
    const estimatedHours = parseFloat(job.estimatedDuration);
    const durationMinutes = Math.max(30, Number.isFinite(estimatedHours) ? Math.round(estimatedHours * 60) : 60);
    const endMinutes = startMinutes + durationMinutes;
    const segments = [];
    for (let segmentStart = startMinutes; segmentStart < endMinutes; segmentStart += 30) {
      const segmentEnd = Math.min(segmentStart + 30, endMinutes);
      segments.push({ start: segmentStart, end: segmentEnd });
    }
    return segments;
  };

  const getPhoneHref = (phone) => {
    if (!phone) return null;
    const sanitized = phone.toString().replace(/\s+/g, '');
    return sanitized ? `tel:${sanitized}` : null;
  };

  useEffect(() => {
    // Reset all sections to closed when job changes (only if it's a different job ID)
    // Don't reset if it's just a timer update (same job ID, different timer data)
    if (selectedJob) {
      const currentJobId = selectedJob._id;
      const prevJobId = selectedJobIdRef?.current;
      
      // Only reset sections if it's a different job, not just a timer update
      if (prevJobId !== currentJobId) {
        console.log('🔄 Calendar: Job changed, resetting accordion sections');
        setJobDetailsOpenSections(prev => ({
          ...jobDetailsInitialSections,
          ...(selectedJob?.status === 'Finished' ? { statusControls: true } : {})
        }));
        selectedJobIdRef.current = currentJobId;
      } else {
        console.log('⏱️ Calendar: Same job, timer update - keeping accordion sections open');
      }
    } else {
      setJobDetailsOpenSections(jobDetailsInitialSections);
      selectedJobIdRef.current = null;
    }
  }, [selectedJob?._id]); // Only depend on job ID, not the entire job object

  // Toggle section - only one section open at a time
  const toggleJobDetailsSection = (key) => {
    setJobDetailsOpenSections((prev) => {
      const currentOpen = Object.keys(prev).find(k => prev[k] === true);
      // If clicking the same section, close it. Otherwise, close all and open the clicked one.
      if (currentOpen === key) {
        return { ...jobDetailsInitialSections };
      } else {
        return {
          ...jobDetailsInitialSections,
          [key]: true
        };
      }
    });
  };

  const rawCalendarDays = generateCalendarDays();
  // Defensive: force a real array so slice/map always exist
  const safeCalendarDaysForMap = toArraySafe(rawCalendarDays);
  
  // Week view days: function that always returns an array
  const getWeekDaysArray = () => {
    try {
      if (!Array.isArray(safeCalendarDaysForMap) || safeCalendarDaysForMap.length === 0) {
        return [];
      }
      const sliced = safeCalendarDaysForMap.slice(0, 6);
      const result = toArraySafe(sliced);
      // Final safety check - ensure it's really an array with map method
      if (!Array.isArray(result) || typeof result.map !== 'function') {
        return [];
      }
      return result;
    } catch (e) {
      return [];
    }
  };
  const monthNames = [
    t('calendar.months.january'), t('calendar.months.february'), t('calendar.months.march'), 
    t('calendar.months.april'), t('calendar.months.may'), t('calendar.months.june'),
    t('calendar.months.july'), t('calendar.months.august'), t('calendar.months.september'), 
    t('calendar.months.october'), t('calendar.months.november'), t('calendar.months.december')
  ];
  
  const monthNamesShort = [
    t('calendar.monthsShort.jan'), t('calendar.monthsShort.feb'), t('calendar.monthsShort.mar'), 
    t('calendar.monthsShort.apr'), t('calendar.monthsShort.may'), t('calendar.monthsShort.jun'),
    t('calendar.monthsShort.jul'), t('calendar.monthsShort.aug'), t('calendar.monthsShort.sep'), 
    t('calendar.monthsShort.oct'), t('calendar.monthsShort.nov'), t('calendar.monthsShort.dec')
  ];

  const dayNames = [
    t('calendar.daysShort.mon'), t('calendar.daysShort.tue'), t('calendar.daysShort.wed'), 
    t('calendar.daysShort.thu'), t('calendar.daysShort.fri'), t('calendar.daysShort.sat'), 
    t('calendar.daysShort.sun')
  ];

  // Get display title based on view mode
  const getDisplayTitle = () => {
    // Create a new Date object to avoid timezone issues
    const date = new Date(currentDate);
    
    if (viewMode === 'day') {
      const monthName = monthNames[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      return `${monthName} ${day}, ${year}`;
    } else if (viewMode === 'week') {
      const startOfWeek = new Date(date);
      const dayOfWeek = date.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      startOfWeek.setDate(date.getDate() + diff);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      const startMonth = monthNames[startOfWeek.getMonth()];
      const endMonth = monthNames[endOfWeek.getMonth()];
      const startDay = startOfWeek.getDate();
      const endDay = endOfWeek.getDate();
      const year = date.getFullYear();
      
      if (startMonth === endMonth) {
        return `${startMonth} ${startDay}-${endDay}, ${year}`;
      } else {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
      }
    } else {
      return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
    }
  };

  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(currentDate.getDate() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() - 7);
    } else {
      newDate.setMonth(currentDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(currentDate.getDate() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() + 7);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const calendarContent = (
    <>
      {/* Header with view mode buttons and navigation - Compact */}
      <div className="flex flex-col gap-2 mb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 min-w-0">
            <h2 className="text-sm sm:text-base font-semibold text-sky-200 tracking-tight hidden sm:block">
              {t('jobs.scheduled')}
            </h2>
            {!isMobile && (
              <div className="flex p-0.5 bg-gray-900/70 border border-white/10 rounded-md">
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-2 py-0.5 text-xs rounded ${
                    viewMode === 'week' ? 'bg-sky-600 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {t('calendar.week')}
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-2 py-0.5 text-xs rounded ${
                    viewMode === 'month' ? 'bg-sky-600 text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {t('calendar.month')}
                </button>
              </div>
            )}
            <div className="flex items-center gap-1">
              <button
                onClick={handlePrevious}
                className="px-2 py-0.5 text-xs bg-gray-900/70 border border-white/10 text-white rounded-md hover:bg-white/10 transition-colors"
                aria-label="Prethodni"
              >
                ←
              </button>
              <DatePicker
                selected={currentDate}
                onChange={(date) => {
                  if (!date) return;
                  const next = new Date(date);
                  if (viewMode === 'month') next.setDate(1);
                  setCurrentDate(next);
                }}
                locale="sr"
                showMonthYearPicker={viewMode === 'month'}
                showPopperArrow={false}
                popperClassName="!z-[80]"
                popperPlacement="bottom"
                wrapperClassName="inline-flex"
                customInput={<CalendarDateButton displayTitle={getDisplayTitle()} />}
              />
              <button
                onClick={handleNext}
                className="px-2 py-0.5 text-xs bg-gray-900/70 border border-white/10 text-white rounded-md hover:bg-white/10 transition-colors"
                aria-label="Sledeći"
              >
                →
              </button>
            </div>
          </div>

          <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-900/70 border border-white/10 rounded-md w-32 sm:w-44">
              <Search className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('jobs.searchJobs')}
                className="bg-transparent border-none outline-none text-xs text-white placeholder-gray-500 w-full min-w-0"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-gray-500 hover:text-white flex-shrink-0"
                  title={t('common.close')}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters((prev) => !prev)}
              className={`relative flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors ${
                showFilters
                  ? 'bg-sky-500/20 text-sky-100 border-sky-400/30'
                  : 'bg-gray-900/70 text-gray-300 hover:text-white hover:bg-white/10 border-white/10'
              }`}
              title={t('jobs.filters')}
            >
              <Filter className="h-3 w-3" />
              <span className="hidden sm:inline">{t('jobs.filters')}</span>
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-gray-700 rounded-lg p-3 sm:p-4 border border-gray-600">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs sm:text-sm font-semibold text-white">{t('jobs.filterJobs')}</h3>
              <button
                onClick={() => {
                  setFilters({
                    worker: [],
                    partner: [],
                    deviceCategory: [],
                    priority: [],
                    warrantyOnly: false,
                    inServiceOnly: false,
                    serviceLocation: [],
                  });
                }}
                className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
              >
                <XCircle className="h-3 w-3" />
                {t('jobs.clearFilters')}
              </button>
            </div>
            
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 ${
                isWorkerScheduler ? 'lg:grid-cols-2' : 'lg:grid-cols-3'
              }`}
            >
              {!isWorkerScheduler && (
              <>
              {/* Worker Filter - Multi-select with checkboxes */}
              <div>
                <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByWorker')}</label>
                <div className="max-h-40 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                  {workers.map((worker) => {
                    const isSelected = Array.isArray(filters.worker) ? filters.worker.includes(worker._id) : filters.worker === worker._id;
                    return (
                      <label key={worker._id} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const currentFilters = Array.isArray(filters.worker) ? filters.worker : (filters.worker ? [filters.worker] : []);
                            const newFilters = e.target.checked
                              ? [...currentFilters, worker._id]
                              : currentFilters.filter(id => id !== worker._id);
                            setFilters(prev => ({ ...prev, worker: newFilters }));
                          }}
                          className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                        />
                        <span>{worker.firstName} {worker.lastName}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Service Partner Filter */}
              <div>
                <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByPartner')}</label>
                <div className="max-h-40 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                  {partners.map((partner) => {
                    const isSelected = Array.isArray(filters.partner) ? filters.partner.includes(partner._id) : filters.partner === partner._id;
                    return (
                      <label key={partner._id} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const currentFilters = Array.isArray(filters.partner) ? filters.partner : (filters.partner ? [filters.partner] : []);
                            const newFilters = e.target.checked
                              ? [...currentFilters, partner._id]
                              : currentFilters.filter(id => id !== partner._id);
                            setFilters(prev => ({ ...prev, partner: newFilters }));
                          }}
                          className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                        />
                        <span>{partner.name || partner.companyName}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              </>
              )}

              {/* Device Category Filter - Multi-select with checkboxes */}
              <div>
                <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByDeviceCategory')}</label>
                <div className="max-h-40 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                  {deviceCategories.map((category) => {
                    const categoryId = category.id || category._id;
                    const isSelected = Array.isArray(filters.deviceCategory) ? filters.deviceCategory.includes(categoryId) : filters.deviceCategory === categoryId;
                    return (
                      <label key={categoryId} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const currentFilters = Array.isArray(filters.deviceCategory) ? filters.deviceCategory : (filters.deviceCategory ? [filters.deviceCategory] : []);
                            const newFilters = e.target.checked
                              ? [...currentFilters, categoryId]
                              : currentFilters.filter(id => id !== categoryId);
                            setFilters(prev => ({ ...prev, deviceCategory: newFilters }));
                          }}
                          className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                        />
                        <span>{category.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Priority Filter - Multi-select with checkboxes */}
              <div>
                <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByPriority')}</label>
                <div className="max-h-40 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                  {['Low', 'Medium', 'High', 'Urgent'].map((priority) => {
                    const isSelected = Array.isArray(filters.priority) ? filters.priority.includes(priority) : filters.priority === priority;
                    return (
                      <label key={priority} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const currentFilters = Array.isArray(filters.priority) ? filters.priority : (filters.priority ? [filters.priority] : []);
                            const newFilters = e.target.checked
                              ? [...currentFilters, priority]
                              : currentFilters.filter(p => p !== priority);
                            setFilters(prev => ({ ...prev, priority: newFilters }));
                          }}
                          className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                        />
                        <span>{t(`jobs.priority${priority}`)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Warranty / In service quick filters */}
              <div>
                <label className="block text-xs text-gray-300 mb-2">{t('jobs.additionalFilters')}</label>
                <div className="bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                  <label className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={!!filters.warrantyOnly}
                      onChange={(e) => setFilters(prev => ({ ...prev, warrantyOnly: e.target.checked }))}
                      className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span>{t('jobs.underWarrantyOnly')}</span>
                  </label>
                  {!isWorkerScheduler && (
                  <label className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={!!filters.inServiceOnly}
                      onChange={(e) => setFilters(prev => ({ ...prev, inServiceOnly: e.target.checked }))}
                      className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span>{t('jobs.inServiceOnly')}</span>
                  </label>
                  )}
                </div>
              </div>

              {isWorkerScheduler && (
              <div>
                <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByServiceLocation')}</label>
                <div className="bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                  {[
                    { value: 'OnSite', label: t('jobs.onSiteAddress') },
                    { value: 'InWorkshop', label: t('jobs.serviceWorkshop') },
                  ].map(({ value, label }) => {
                    const isSelected = Array.isArray(filters.serviceLocation) && filters.serviceLocation.includes(value);
                    return (
                      <label key={value} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const current = Array.isArray(filters.serviceLocation) ? filters.serviceLocation : [];
                            const next = e.target.checked
                              ? [...current, value]
                              : current.filter((v) => v !== value);
                            setFilters((prev) => ({ ...prev, serviceLocation: next }));
                          }}
                          className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                        />
                        <span>{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              )}
            </div>

            {/* Active Filters Display */}
            {((!isWorkerScheduler && (Array.isArray(filters.worker) ? filters.worker.length > 0 : filters.worker)) || 
              (!isWorkerScheduler && (Array.isArray(filters.partner) ? filters.partner.length > 0 : filters.partner)) || 
              (Array.isArray(filters.deviceCategory) ? filters.deviceCategory.length > 0 : filters.deviceCategory) || 
              (Array.isArray(filters.priority) ? filters.priority.length > 0 : filters.priority) ||
              filters.warrantyOnly || (!isWorkerScheduler && filters.inServiceOnly) ||
              (isWorkerScheduler && Array.isArray(filters.serviceLocation) && filters.serviceLocation.length > 0)) && (
              <div className="mt-3 pt-3 border-t border-gray-600">
                <div className="flex flex-wrap gap-2">
                  {/* Worker filters */}
                  {!isWorkerScheduler && Array.isArray(filters.worker) ? filters.worker.map(workerId => {
                    const worker = workers.find(w => String(w._id) === String(workerId));
                    const name = worker ? `${worker.firstName} ${worker.lastName}` : workerId;
                    return (
                      <span key={workerId} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                        {t('jobs.worker')}: {name}
                        <button
                          onClick={() => {
                            const newFilters = filters.worker.filter(id => id !== workerId);
                            setFilters(prev => ({ ...prev, worker: newFilters }));
                          }}
                          className="hover:text-gray-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  }) : !isWorkerScheduler && filters.worker && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                      {t('jobs.worker')}: {getWorkerName(filters.worker, workers, partners)}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, worker: [] }))}
                        className="hover:text-gray-200"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}

                  {/* Partner filters */}
                  {!isWorkerScheduler && Array.isArray(filters.partner) ? filters.partner.map(partnerId => {
                    const partner = partners.find(p => String(p._id) === String(partnerId));
                    const name = partner ? (partner.name || partner.companyName) : partnerId;
                    return (
                      <span key={partnerId} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded">
                        {t('jobs.partner')}: {name}
                        <button
                          onClick={() => {
                            const newFilters = filters.partner.filter(id => id !== partnerId);
                            setFilters(prev => ({ ...prev, partner: newFilters }));
                          }}
                          className="hover:text-gray-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  }) : !isWorkerScheduler && filters.partner && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded">
                      {t('jobs.partner')}: {filters.partner}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, partner: [] }))}
                        className="hover:text-gray-200"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                  
                  {/* Device Category filters */}
                  {Array.isArray(filters.deviceCategory) ? filters.deviceCategory.map(categoryId => {
                    const category = deviceCategories.find(cat => String(cat.id || cat._id) === String(categoryId));
                    return (
                      <span key={categoryId} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                        {t('jobs.category')}: {category?.name || categoryId}
                        <button
                          onClick={() => {
                            const newFilters = filters.deviceCategory.filter(id => id !== categoryId);
                            setFilters(prev => ({ ...prev, deviceCategory: newFilters }));
                          }}
                          className="hover:text-gray-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  }) : filters.deviceCategory && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                      {t('jobs.category')}: {deviceCategories.find(cat => (cat.id || cat._id) === filters.deviceCategory)?.name || filters.deviceCategory}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, deviceCategory: [] }))}
                        className="hover:text-gray-200"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}
                  
                  {/* Priority filters */}
                  {Array.isArray(filters.priority) ? filters.priority.map(priority => (
                    <span key={priority} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                      {t('jobs.priority')}: {t(`jobs.priority${priority}`)}
                      <button
                        onClick={() => {
                          const newFilters = filters.priority.filter(p => p !== priority);
                          setFilters(prev => ({ ...prev, priority: newFilters }));
                        }}
                        className="hover:text-gray-200"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )) : filters.priority && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                      {t('jobs.priority')}: {t(`jobs.priority${filters.priority}`)}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, priority: [] }))}
                        className="hover:text-gray-200"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}

                  {filters.warrantyOnly && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 text-white rounded">
                      {t('jobs.underWarrantyOnly')}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, warrantyOnly: false }))}
                        className="hover:text-gray-200"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}

                  {!isWorkerScheduler && filters.inServiceOnly && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-indigo-600 text-white rounded">
                      {t('jobs.inServiceOnly')}
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, inServiceOnly: false }))}
                        className="hover:text-gray-200"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  )}

                  {isWorkerScheduler &&
                    Array.isArray(filters.serviceLocation) &&
                    filters.serviceLocation.map((loc) => (
                      <span
                        key={loc}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-600 text-white rounded"
                      >
                        {t('jobs.serviceLocation')}:{' '}
                        {loc === 'InWorkshop' ? t('jobs.serviceWorkshop') : t('jobs.onSiteAddress')}
                        <button
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              serviceLocation: (prev.serviceLocation || []).filter((v) => v !== loc),
                            }))
                          }
                          className="hover:text-gray-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Jobs Count */}
        <div className="text-xs sm:text-sm text-gray-400">
          {(() => {
            // Count relevant jobs - mora da koristi iste status kriterijume kao filteredJobs
            // (Finished poslovi ostaju vidljivi dok se ne arhiviraju), inače dobijemo "4 od 3".
            const relevantJobs = jobs?.filter(job => {
              if (job.status === 'Archived' || job.status === 'Completed' || job.status === 'Cancelled') return false;
              if (!job.serviceDate) return false;
              return true;
            }) || [];
            
            return filteredJobs.length === 0 ? (
              <span>{t('jobs.noJobsMatch')}</span>
            ) : (
              <span>
                {filteredJobs.length} {filteredJobs.length === 1 ? t('jobs.jobFound') : t('jobs.jobsFound')}
                {relevantJobs.length !== filteredJobs.length && (
                  <span className="text-gray-500 ml-1">
                    ({t('jobs.outOf')} {relevantJobs.length})
                  </span>
                )}
              </span>
            );
          })()}
        </div>
      </div>

        {/* Week View - Special Layout: 6 days in 2 rows, Sunday wide below */}
        {viewMode === 'week' ? (
          <div className="space-y-2">
            {/* First 6 days - 2 rows of 3 */}
            <div className="grid grid-cols-3 gap-2">
              {(() => {
                const weekDays = getWeekDaysArray();
                return Array.isArray(weekDays) && typeof weekDays.map === 'function' 
                  ? weekDays.map((date, index) => {
                const isToday = date.toDateString() === new Date().toDateString();
                const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                const dayJobs = getJobsForDate(date);

                return (
                  <div key={index} className="flex flex-col">
                    {/* Day Name */}
                    <div 
                      className="p-1 text-center text-xs font-medium text-gray-400 bg-gray-800 rounded-t flex items-center justify-center"
                      style={{ 
                        writingMode: 'horizontal-tb', 
                        textOrientation: 'mixed',
                        flexDirection: 'row',
                        whiteSpace: 'nowrap',
                        transform: 'none',
                        letterSpacing: 'normal'
                      }}
                    >
                      {dayNames[index]}
                    </div>
                    {/* Day Content */}
                    <div
                      className={`${dayJobs.length === 0 ? 'min-h-0' : 'min-h-32'} p-2 border border-gray-600 rounded-b bg-gray-700 ${
                        isToday ? 'ring-2 ring-blue-500' : ''
                      } ${isSelected ? 'bg-blue-600' : ''}`}
                      onClick={() => {
                        setSelectedDate(date);
                        // In week view, open modal for first job if available
                        if (viewMode === 'week' && dayJobs.length > 0) {
                          setSelectedJob(dayJobs[0]);
                        }
                      }}
                    >
                      {/* Date Header */}
                      <div className="text-sm text-gray-300 mb-2 font-medium">
                        {monthNamesShort[date.getMonth()]} {date.getDate()}
                      </div>
                      
                      {/* Jobs List */}
                      <div className="space-y-1.5 flex flex-col">
                        {dayJobs.map(job => {
                          const workerColor = job.assignedTo ? getWorkerColor(job.assignedTo, workers) : null;
                          const workerInitials = job.assignedTo ? getWorkerInitials(job.assignedTo, workers, partners) : null;
                          const isEditing = editingJobId === job._id;
                          
                          return (
                            <div
                              key={job._id}
                              onClick={(e) => !isEditing && job.status !== 'Finished' && handleJobClick(job, e)}
                              className={`text-xs p-1.5 rounded transition-opacity flex items-center gap-1 sm:gap-2 w-full min-w-0 overflow-hidden ${
                                isEditing && job.status !== 'Finished'
                                  ? 'bg-blue-50 border-2 border-blue-500' 
                                  : job.status === 'Finished' 
                                    ? 'bg-green-200 text-green-900 border-2 border-green-400 cursor-pointer hover:opacity-80'
                                    : job.status === 'Archived'
                                      ? 'bg-gray-300 text-gray-700 cursor-pointer hover:opacity-80'
                                      : `cursor-pointer hover:opacity-80 ${job.isEmergency === 'yes' ? 'bg-red-100 text-red-900' : 'bg-white text-blue-900'}`
                              }`}
                            >
                              {job.status === 'Finished' ? (
                                <>
                                  <span className="font-semibold text-[11px] text-blue-700 whitespace-nowrap flex-shrink-0">
                                    {job.scheduledTime ? (job.scheduledTime.length === 5 ? job.scheduledTime : job.scheduledTime.substring(0, 5)) : 'No time'}
                                  </span>
                                  <span className={`text-[10px] truncate flex-1 min-w-0`} title={`${formatDisplayValue(job.clientAddress || job.clientName)}${formatDisplayValue(job.issueDescription) !== '-' ? ' - ' + formatDisplayValue(job.issueDescription) : ''}`}>
                                    {searchQuery && searchQuery.trim() ? (
                                      highlightText(formatDisplayValue(job.clientAddress || job.clientName), searchQuery)
                                    ) : (
                                      formatDisplayValue(job.clientAddress || job.clientName)
                                    )}
                                    {job.issueDescription && !isMobile && (
                                      <span className="text-gray-500"> - {job.issueDescription.length > 20 ? job.issueDescription.substring(0, 20) + '...' : job.issueDescription}</span>
                                    )}
                                  </span>
                                  <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                                    {renderWorkerBadge(job, workerColor, workerInitials)}
                                    <DeadlineMarker job={job} size="sm" />
                                  </div>
                                  {renderJobListButton(job)}
                                </>
                              ) : isEditing ? (
                                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1">
                                  <div className="calendar-inline-edit-inputs flex gap-2 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                                    <DatePickerField
                                      value={editingJobDateTime.date || ''}
                                      onChange={(dateStr) => setEditingJobDateTime(prev => ({ ...prev, date: dateStr }))}
                                      placeholder="dd.mm.yyyy"
                                      inputClass="text-[10px] px-1 py-0.5 bg-white text-gray-900 border border-gray-300 rounded flex-1 min-w-0 w-full"
                                    />
                                    <TimePickerField
                                      value={editingJobDateTime.time || '09:00'}
                                      onChange={(timeStr) => setEditingJobDateTime(prev => ({ ...prev, time: (timeStr || '').substring(0, 5) }))}
                                      placeholder="HH:mm"
                                      inputClass="text-[10px] px-1 py-0.5 bg-white text-gray-900 border border-gray-300 rounded flex-1 min-w-0 w-full"
                                    />
                                  </div>
                                  <div className="flex flex-row items-center gap-1 justify-center flex-wrap">
                                    <button onClick={(e) => { e.stopPropagation(); handleInlineDateTimeSave(job._id); }} className="px-2 py-0.5 bg-green-500 text-white text-[10px] rounded hover:bg-green-600 min-w-[28px]" title="Sačuvaj">✓</button>
                                    <button onClick={(e) => { e.stopPropagation(); handleInlineDateTimeCancel(); }} className="px-2 py-0.5 bg-red-500 text-white text-[10px] rounded hover:bg-red-600 min-w-[28px]" title="Otkaži">✕</button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); if (onJobSelect) onJobSelect(job); else setSelectedJob(job); }}
                                      className="px-1.5 py-0.5 bg-blue-500 text-white text-[10px] rounded hover:bg-blue-600 flex items-center justify-center min-w-[28px]"
                                      title="Detalji posla"
                                    >
                                      <List className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                            ) : (
                              <>
                                <span className="font-semibold text-[11px] text-blue-700 whitespace-nowrap flex-shrink-0">
                                  {job.scheduledTime ? (job.scheduledTime.length === 5 ? job.scheduledTime : job.scheduledTime.substring(0, 5)) : 'No time'}
                                </span>
                                <span className={`text-[10px] truncate flex-1 min-w-0 ${highlightedJobId === job._id ? 'ring-2 ring-yellow-400 rounded px-1' : ''}`} title={`${formatDisplayValue(job.clientAddress || job.clientName)}${formatDisplayValue(job.issueDescription) !== '-' ? ' - ' + formatDisplayValue(job.issueDescription) : ''}`}>
                                  {searchQuery && searchQuery.trim() ? (
                                    highlightText(formatDisplayValue(job.clientAddress || job.clientName), searchQuery)
                                  ) : (
                                    formatDisplayValue(job.clientAddress || job.clientName)
                                  )}
                                  {job.issueDescription && !isMobile && (
                                    <span className="text-gray-500"> - {job.issueDescription.length > 20 ? job.issueDescription.substring(0, 20) + '...' : job.issueDescription}</span>
                                  )}
                                </span>
                                <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                                  {workerColor && workerInitials && (
                                    <div 
                                      className="h-4 w-4 rounded-full inline-flex items-center justify-center text-white text-[8px] font-semibold flex-shrink-0 overflow-hidden leading-none"
                                      style={{ backgroundColor: workerColor, background: workerColor }}
                                      title={getWorkerName(job.assignedTo, workers, partners)}
                                    >
                                      {workerInitials}
                                    </div>
                                  )}
                                  <DeadlineMarker job={job} size="sm" />
                                  {!isWorkerScheduler && renderJobListButton(job)}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
                  })
                  : [];
              })()}
            </div>
            {/* Sunday (7th day) - wide row */}
            {Array.isArray(safeCalendarDaysForMap) && safeCalendarDaysForMap.length > 6 && (() => {
              const date = safeCalendarDaysForMap[6];
              const isToday = date.toDateString() === new Date().toDateString();
              const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
              const dayJobs = getJobsForDate(date);

              return (
                <div className="flex flex-col">
                  {/* Day Name */}
                  <div 
                    className="p-1 text-center text-xs font-medium text-gray-400 bg-gray-800 rounded-t flex items-center justify-center"
                    style={{ 
                      writingMode: 'horizontal-tb', 
                      textOrientation: 'mixed',
                      flexDirection: 'row',
                      whiteSpace: 'nowrap',
                      transform: 'none',
                      letterSpacing: 'normal'
                    }}
                  >
                    {dayNames[6]}
                  </div>
                  {/* Day Content - Wide and Short */}
                  <div
                    className={`${dayJobs.length === 0 ? 'min-h-0' : 'min-h-20'} p-2 border border-gray-600 rounded-b bg-gray-700 ${
                      isToday ? 'ring-2 ring-blue-500' : ''
                    } ${isSelected ? 'bg-blue-600' : ''}`}
                    onClick={(e) => {
                      // Only set selected date if clicking on the container itself, not on a job
                      if (e.target === e.currentTarget || e.target.closest('.job-item') === null) {
                        setSelectedDate(date);
                        // In week view, open modal for first job if available
                        if (viewMode === 'week' && dayJobs.length > 0) {
                          setSelectedJob(dayJobs[0]);
                        }
                      }
                    }}
                  >
                    {/* Date Header */}
                    <div className="text-sm text-gray-300 mb-2 font-medium inline-block mr-4">
                      {monthNamesShort[date.getMonth()]} {date.getDate()}
                    </div>
                    
                    {/* Jobs List - Vertical (one below another) */}
                    <div className="flex flex-col gap-2">
                      {dayJobs.map(job => {
                        const workerColor = job.assignedTo ? getWorkerColor(job.assignedTo, workers) : null;
                        const workerInitials = job.assignedTo ? getWorkerInitials(job.assignedTo, workers, partners) : null;
                        const isEditing = editingJobId === job._id;
                        
                        return (
                          <div
                            key={job._id}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isEditing && job.status !== 'Finished') {
                                handleJobClick(job, e);
                              }
                            }}
                            className={`job-item text-xs p-1.5 rounded transition-opacity flex items-center gap-1 sm:gap-2 w-full min-w-0 overflow-hidden ${
                              isEditing && job.status !== 'Finished'
                                ? 'bg-blue-50 border-2 border-blue-500' 
                                : job.status === 'Finished' 
                                  ? 'bg-green-200 text-green-900 border-2 border-green-400 cursor-pointer hover:opacity-80'
                                  : job.status === 'Archived'
                                    ? 'bg-gray-300 text-gray-700 cursor-pointer hover:opacity-80'
                                    : `cursor-pointer hover:opacity-80 ${job.isEmergency === 'yes' ? 'bg-red-100 text-red-900' : 'bg-white text-blue-900'}`
                            }`}
                          >
                            {job.status === 'Finished' ? (
                              <>
                                <span className="font-semibold text-[11px] text-blue-700 flex-shrink-0">
                                  {job.scheduledTime ? (job.scheduledTime.length === 5 ? job.scheduledTime : job.scheduledTime.substring(0, 5)) : 'No time'}
                                </span>
                                <span className={`text-[10px] flex-1 truncate min-w-0`} title={`${job.clientAddress || job.clientName || '-'}${formatDisplayValue(job.issueDescription) !== '-' ? ' - ' + formatDisplayValue(job.issueDescription) : ''}`}>
                                  {searchQuery && searchQuery.trim() ? (
                                    highlightText(formatDisplayValue(job.clientAddress || job.clientName), searchQuery)
                                  ) : (
                                    formatDisplayValue(job.clientAddress || job.clientName)
                                  )}
                                  {job.issueDescription && !isMobile && (
                                    <span className="text-gray-500"> - {job.issueDescription.length > 20 ? job.issueDescription.substring(0, 20) + '...' : job.issueDescription}</span>
                                  )}
                                </span>
                                <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                                  {workerColor && workerInitials && (
                                    <div 
                                      className="h-4 w-4 rounded-full inline-flex items-center justify-center text-white text-[8px] font-semibold flex-shrink-0 overflow-hidden leading-none"
                                      style={{ backgroundColor: workerColor, background: workerColor }}
                                      title={getWorkerName(job.assignedTo, workers, partners)}
                                    >
                                      {workerInitials}
                                    </div>
                                  )}
                                  <DeadlineMarker job={job} size="sm" />
                                </div>
                                {renderJobListButton(job)}
                              </>
                            ) : isEditing ? (
                              <div className="calendar-inline-edit-inputs flex flex-col gap-1.5 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                                <div className="flex items-center gap-2 w-full">
                                  <div className="min-w-[100px] flex-1">
                                    <DatePickerField
                                      value={editingJobDateTime.date || ''}
                                      onChange={(dateStr) => setEditingJobDateTime(prev => ({ ...prev, date: dateStr }))}
                                      placeholder="dd.mm.yyyy"
                                      inputClass="text-[10px] px-1 py-0.5 bg-white text-gray-900 border border-gray-300 rounded w-full"
                                    />
                                  </div>
                                  <div className="min-w-[70px] flex-1">
                                    <TimePickerField
                                      value={editingJobDateTime.time || '09:00'}
                                      onChange={(timeStr) => setEditingJobDateTime(prev => ({ ...prev, time: (timeStr || '').substring(0, 5) }))}
                                      placeholder="HH:mm"
                                      inputClass="text-[10px] px-1 py-0.5 bg-white text-gray-900 border border-gray-300 rounded w-full"
                                    />
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleInlineDateTimeSave(job._id); }}
                                    className="h-6 w-6 bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center"
                                    title="Potvrdi datum i vreme"
                                  >
                                    <CheckCircle className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleInlineDateTimeCancel(); }}
                                    className="h-6 w-6 bg-red-500 text-white rounded hover:bg-red-600 flex items-center justify-center"
                                    title="Zatvori"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleInlineDateTimeCancel(); setEditingJob(job); }}
                                    className="h-6 w-6 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center"
                                    title="Izmeni podatke intervencije"
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <span className="font-semibold text-[11px] text-blue-700 flex-shrink-0">
                                  {job.scheduledTime ? (job.scheduledTime.length === 5 ? job.scheduledTime : job.scheduledTime.substring(0, 5)) : 'No time'}
                                </span>
                                <span className={`text-[10px] flex-1 truncate min-w-0 ${highlightedJobId === job._id ? 'ring-2 ring-yellow-400 rounded px-1' : ''}`} title={`${job.clientAddress || job.clientName || '-'}${formatDisplayValue(job.issueDescription) !== '-' ? ' - ' + formatDisplayValue(job.issueDescription) : ''}`}>
                                  {searchQuery && searchQuery.trim() ? (
                                    highlightText(formatDisplayValue(job.clientAddress || job.clientName), searchQuery)
                                  ) : (
                                    formatDisplayValue(job.clientAddress || job.clientName)
                                  )}
                                  {job.issueDescription && !isMobile && (
                                    <span className="text-gray-500"> - {job.issueDescription.length > 20 ? job.issueDescription.substring(0, 20) + '...' : job.issueDescription}</span>
                                  )}
                                </span>
                                <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                                  {workerColor && workerInitials && (
                                    <div 
                                      className="h-4 w-4 rounded-full inline-flex items-center justify-center text-white text-[8px] font-semibold flex-shrink-0 overflow-hidden leading-none"
                                      style={{ backgroundColor: workerColor, background: workerColor }}
                                      title={getWorkerName(job.assignedTo, workers, partners)}
                                    >
                                      {workerInitials}
                                    </div>
                                  )}
                                  <DeadlineMarker job={job} size="sm" />
                                  {!isWorkerScheduler && renderJobListButton(job)}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
            </div>
          ) : (
          <>
            {/* Day Names Header */}
            {viewMode !== 'day' && (
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map(day => (
                  <div 
                    key={day} 
                    className="p-2 text-sm font-medium text-gray-400"
                    style={{ 
                      writingMode: 'horizontal-tb', 
                      textOrientation: 'mixed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'row',
                      whiteSpace: 'nowrap',
                      transform: 'none',
                      letterSpacing: 'normal',
                      direction: 'ltr',
                      unicodeBidi: 'normal'
                    }}
                  >
                    <span style={{ display: 'inline-block', transform: 'none' }}>{day}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Calendar Grid */}
            <div className={`grid ${
              viewMode === 'day' ? 'grid-cols-1' : 'grid-cols-7'
            } gap-2`}>
              {safeCalendarDaysForMap.map((date, index) => {
                const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                const isToday = date.toDateString() === new Date().toDateString();
                const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                const dayJobs = getJobsForDate(date);

                return (
                  <div
                    key={index}
                    className={`p-2 border border-gray-600 rounded ${
                      isCurrentMonth || viewMode !== 'month' ? 'bg-gray-700' : 'bg-gray-800'
                    } ${isToday ? 'ring-2 ring-blue-500' : ''} ${
                      isSelected ? 'bg-blue-600' : ''
                    } ${viewMode !== 'day' ? 'min-h-20' : ''}`}
                    onClick={() => setSelectedDate(date)}
                  >
                    {/* Date Header - only for month/week view */}
                    {viewMode !== 'day' && (
                      <div className="text-sm text-gray-300 mb-2 font-medium">
                        {monthNamesShort[date.getMonth()]} {date.getDate()}
                      </div>
                    )}
                    
                    {/* Jobs List */}
                    <div className="space-y-1 flex flex-col">
                      {/* Show all jobs in month view, limited in day view */}
                      {dayJobs.slice(0, viewMode === 'day' ? 20 : undefined).map(job => {
                        const workerColor = job.assignedTo ? getWorkerColor(job.assignedTo, workers) : null;
                        const workerInitials = job.assignedTo ? getWorkerInitials(job.assignedTo, workers, partners) : null;
                        const isEditing = editingJobId === job._id;
                        
                        return (
                          <div
                            key={job._id}
                            onClick={(e) => !isEditing && job.status !== 'Finished' && handleJobClick(job, e)}
                            className={`text-xs p-1.5 rounded transition-opacity flex items-center gap-1 sm:gap-2 w-full min-w-0 overflow-hidden ${
                              isEditing && job.status !== 'Finished'
                                ? 'bg-blue-50 border-2 border-blue-500' 
                                : job.status === 'Finished' 
                                  ? 'bg-green-200 text-green-900 border-2 border-green-400 cursor-pointer hover:opacity-80'
                                  : job.status === 'Archived'
                                    ? 'bg-gray-300 text-gray-700 cursor-pointer hover:opacity-80'
                                    : `cursor-pointer hover:opacity-80 ${job.isEmergency === 'yes' ? 'bg-red-100 text-red-900' : 'bg-white text-blue-900'}`
                            }`}
                          >
                            {job.status === 'Finished' ? (
                              <div className={`flex flex-row items-center gap-1 w-full min-w-0 overflow-hidden ${viewMode === 'month' ? 'flex-wrap' : 'flex-nowrap'}`}>
                                <span className="font-semibold text-[11px] text-blue-700 whitespace-nowrap flex-shrink-0">
                                  {job.scheduledTime ? (job.scheduledTime.length === 5 ? job.scheduledTime : job.scheduledTime.substring(0, 5)) : 'No time'}
                                </span>
                                <span className="text-[10px] truncate flex-1 min-w-0" title={`${formatDisplayValue(job.clientAddress || job.clientName)}${formatDisplayValue(job.issueDescription) !== '-' ? ' - ' + formatDisplayValue(job.issueDescription) : ''}`}>
                                  {searchQuery && searchQuery.trim() ? (
                                    highlightText(formatDisplayValue(job.clientAddress || job.clientName), searchQuery)
                                  ) : (
                                    formatDisplayValue(job.clientAddress || job.clientName)
                                  )}
                                  {job.issueDescription && !isMobile && (
                                    <span className="text-gray-500"> - {job.issueDescription.length > 20 ? job.issueDescription.substring(0, 20) + '...' : job.issueDescription}</span>
                                  )}
                                </span>
                                <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                                  {workerColor && workerInitials && (
                                    <div 
                                      className="h-4 w-4 rounded-full inline-flex items-center justify-center text-white text-[8px] font-semibold flex-shrink-0 overflow-hidden leading-none"
                                      style={{ backgroundColor: workerColor, background: workerColor }}
                                      title={getWorkerName(job.assignedTo, workers, partners)}
                                    >
                                      {workerInitials}
                                    </div>
                                  )}
                                  <DeadlineMarker job={job} size="sm" />
                                </div>
                                {renderJobListButton(job)}
                              </div>
                            ) : isEditing ? (
                              <div className={`calendar-inline-edit-inputs flex flex-row items-center gap-0.25 ${viewMode === 'month' ? 'w-full flex-wrap' : 'flex-nowrap'}`} onClick={(e) => e.stopPropagation()}>
                                <div className="flex-shrink-0 min-w-0" style={{ maxWidth: '85px' }}>
                                  <DatePickerField
                                    value={editingJobDateTime.date || ''}
                                    onChange={(dateStr) => setEditingJobDateTime(prev => ({ ...prev, date: dateStr }))}
                                    placeholder="dd.mm.yyyy"
                                    inputClass="text-[7px] px-0.5 py-0 bg-white text-gray-900 border border-gray-300 rounded w-full"
                                  />
                                </div>
                                <div className="flex-shrink-0 min-w-0" style={{ maxWidth: '55px' }}>
                                  <TimePickerField
                                    value={editingJobDateTime.time || '09:00'}
                                    onChange={(timeStr) => setEditingJobDateTime(prev => ({ ...prev, time: (timeStr || '').substring(0, 5) }))}
                                    placeholder="HH:mm"
                                    inputClass="text-[7px] px-0.5 py-0 bg-white text-gray-900 border border-gray-300 rounded w-full"
                                  />
                                </div>
                                <div className="flex flex-row items-center gap-0.25 justify-center flex-shrink-0">
                                  <button onClick={(e) => { e.stopPropagation(); handleInlineDateTimeSave(job._id); }} className="px-0.5 py-0 bg-green-500 text-white text-[7px] rounded hover:bg-green-600 min-w-[16px] h-[16px]" title="Sačuvaj">✓</button>
                                  <button onClick={(e) => { e.stopPropagation(); handleInlineDateTimeCancel(); }} className="px-0.5 py-0 bg-red-500 text-white text-[7px] rounded hover:bg-red-600 min-w-[16px] h-[16px]" title="Otkaži">✕</button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); if (onJobSelect) onJobSelect(job); else setSelectedJob(job); }}
                                    className="px-0.5 py-0 bg-blue-500 text-white text-[7px] rounded hover:bg-blue-600 flex items-center justify-center min-w-[16px] h-[16px]"
                                    title="Detalji posla"
                                  >
                                    <List className="h-2.5 w-2.5" />
                                  </button>
                                </div>
                              </div>
                            ) : viewMode === 'day' ? (
                              <div className="flex items-center gap-2 min-w-0 w-full">
                                <span className="font-semibold text-blue-700 whitespace-nowrap flex-shrink-0">
                                  {job.scheduledTime || 'No time'}
                                  {job.estimatedDuration && !isMobile && (
                                    <span className="text-[9px] text-blue-500 ml-1">({job.estimatedDuration}h)</span>
                                  )}
                                </span>
                                <span className={`text-[10px] truncate flex-1 min-w-0 ${highlightedJobId === job._id ? 'ring-2 ring-yellow-400 rounded px-1' : ''}`} title={`${formatDisplayValue(job.clientAddress || job.clientName)}${formatDisplayValue(job.issueDescription) !== '-' ? ' - ' + formatDisplayValue(job.issueDescription) : ''}`}>
                                  {searchQuery && searchQuery.trim() ? (
                                    highlightText(formatDisplayValue(job.clientAddress || job.clientName), searchQuery)
                                  ) : (
                                    formatDisplayValue(job.clientAddress || job.clientName)
                                  )}
                                  {job.issueDescription && !isMobile && (
                                    <span className="text-gray-500"> - {job.issueDescription.length > 20 ? job.issueDescription.substring(0, 20) + '...' : job.issueDescription}</span>
                                  )}
                                </span>
                                <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                                  {workerColor && workerInitials && (
                                    <div 
                                      className="h-5 w-5 rounded-full inline-flex items-center justify-center text-white text-[9px] font-semibold flex-shrink-0 overflow-hidden leading-none"
                                      style={{ backgroundColor: workerColor, background: workerColor }}
                                      title={getWorkerName(job.assignedTo, workers, partners)}
                                    >
                                      {workerInitials}
                                    </div>
                                  )}
                                  <DeadlineMarker job={job} size="sm" />
                                  {!isWorkerScheduler && renderJobListButton(job)}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 min-w-0 w-full">
                                <span className="font-semibold text-[11px] text-blue-700 whitespace-nowrap flex-shrink-0">
                                  {job.scheduledTime ? (job.scheduledTime.length === 5 ? job.scheduledTime : job.scheduledTime.substring(0, 5)) : 'No time'}
                                </span>
                                <span className={`text-[10px] truncate flex-1 min-w-0 ${highlightedJobId === job._id ? 'ring-2 ring-yellow-400 rounded px-1' : ''}`} title={`${formatDisplayValue(job.clientAddress || job.clientName)}${formatDisplayValue(job.issueDescription) !== '-' ? ' - ' + formatDisplayValue(job.issueDescription) : ''}`}>
                                  {searchQuery && searchQuery.trim() ? (
                                    highlightText(formatDisplayValue(job.clientAddress || job.clientName), searchQuery)
                                  ) : (
                                    formatDisplayValue(job.clientAddress || job.clientName)
                                  )}
                                  {job.issueDescription && !isMobile && (
                                    <span className="text-gray-500"> - {job.issueDescription.length > 20 ? job.issueDescription.substring(0, 20) + '...' : job.issueDescription}</span>
                                  )}
                                </span>
                                <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                                  {workerColor && workerInitials && (
                                    <div 
                                      className="h-4 w-4 rounded-full inline-flex items-center justify-center text-white text-[8px] font-semibold flex-shrink-0 overflow-hidden leading-none"
                                      style={{ backgroundColor: workerColor, background: workerColor }}
                                      title={getWorkerName(job.assignedTo, workers, partners)}
                                    >
                                      {workerInitials}
                                    </div>
                                  )}
                                  <DeadlineMarker job={job} size="sm" />
                                  {!isWorkerScheduler && renderJobListButton(job)}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
    </>
  );

  return (
    <div className="bg-gray-800/90 rounded-xl p-2 sm:p-4 h-full border border-white/5 border-t-2 border-t-sky-500/80 shadow-lg shadow-black/10">
      {isNested ? (
        calendarContent
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          {calendarContent}
        </DragDropContext>
      )}

      {/* Job Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-white flex items-baseline gap-2 flex-wrap">
                <span>{t('jobs.jobDetails')}</span>
                {formatWorkOrderNumber(selectedJob) ? (
                  <span className="text-base font-semibold text-blue-300">
                    {formatWorkOrderNumber(selectedJob)}
                  </span>
                ) : null}
              </h3>
              <button
                onClick={() => {
                  setIsPrintModalOpen(false);
                  setSelectedJob(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Client Information */}
              <div className="rounded-md border border-gray-700 bg-gray-800">
                <button
                  type="button"
                  onClick={() => toggleJobDetailsSection('clientInfo')}
                  className="flex w-full items-center justify-between px-3 py-2 text-left"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-100">
                    <User className="h-4 w-4" />
                    {t('jobs.clientInfo')}
                  </span>
                  <svg
                    className={`h-4 w-4 text-gray-400 transition-transform transform ${jobDetailsOpenSections.clientInfo ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {jobDetailsOpenSections.clientInfo && (
                  <div className="border-t border-gray-700 px-4 py-3 text-sm text-gray-300 space-y-2">
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[100px]">{t('jobs.name')}:</span>
                      <span className="flex-1 font-medium">{formatDisplayValue(selectedJob.clientName)}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[100px]">{t('jobs.phone')}:</span>
                      <span className="flex-1">
                        {selectedJob.clientPhone ? (
                          <a
                            href={getPhoneHref(selectedJob.clientPhone) || undefined}
                            className="text-blue-300 hover:text-blue-200 underline underline-offset-2"
                          >
                            {selectedJob.clientPhone}
                          </a>
                        ) : (
                          '-'
                        )}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[100px]">{t('jobs.email')}:</span>
                      <span className="flex-1">
                        {selectedJob.clientEmail && selectedJob.clientEmail !== 'no-email@example.com' && selectedJob.clientEmail.trim()
                          ? selectedJob.clientEmail
                          : '-'}
                      </span>
                    </div>
                    {selectedJob.clientAddress && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 min-w-[100px]">{t('jobs.address')}:</span>
                        <span className="flex-1">{selectedJob.clientAddress}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Service Details */}
              <div className="rounded-md border border-gray-700 bg-gray-800">
                <button
                  type="button"
                  onClick={() => toggleJobDetailsSection('serviceDetails')}
                  className="flex w-full items-center justify-between px-3 py-2 text-left"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-100">
                    <Calendar className="h-4 w-4" />
                    {t('jobs.serviceDetails')}
                  </span>
                  <svg
                    className={`h-4 w-4 text-gray-400 transition-transform transform ${jobDetailsOpenSections.serviceDetails ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {jobDetailsOpenSections.serviceDetails && (
                  <div className="border-t border-gray-700 px-4 py-3 text-sm text-gray-300 space-y-2">
                    {selectedJob.serviceDate && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 min-w-[120px]">{t('jobs.date')}:</span>
                        <span className="flex-1">{new Date(selectedJob.serviceDate).toLocaleDateString('sr-RS')}</span>
                      </div>
                    )}
                    {selectedJob.scheduledTime && (
                      <div className="flex gap-2">
                        <span className="text-gray-400 min-w-[120px]">{t('jobs.time')}:</span>
                        <span className="flex-1">
                          {(() => {
                            const [startHour, startMinute] = selectedJob.scheduledTime.split(':').map(Number);
                            let endTime;
                            if (selectedJob.estimatedDuration) {
                              const durationHours = parseFloat(selectedJob.estimatedDuration);
                              const durationMinutes = Math.round(durationHours * 60);
                              const startMinutes = startHour * 60 + startMinute;
                              const endMinutes = startMinutes + durationMinutes;
                              const endHour = Math.floor(endMinutes / 60) % 24;
                              const endMin = endMinutes % 60;
                              endTime = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
                            } else {
                              const endHour = (startHour + 1) % 24;
                              endTime = `${endHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
                            }
                            return `${selectedJob.scheduledTime} - ${endTime}`;
                          })()}
                        </span>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[120px]">{t('jobs.status')}:</span>
                      <span className="flex-1">{getJobDisplayStatus(selectedJob)}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-gray-400 min-w-[120px]">{t('jobs.priority')}:</span>
                      <span className="flex-1">{selectedJob.priority}</span>
                    </div>
                    {selectedJob.assignedTo && (
                        <div className="flex gap-2 items-center">
                        <span className="text-gray-400 min-w-[120px]">{t('jobs.assignedTo')}:</span>
                        <div className="flex-1 flex items-center gap-2">
                          <div 
                            className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 shadow-sm"
                            style={{ backgroundColor: getWorkerColor(selectedJob.assignedTo, workers), background: getWorkerColor(selectedJob.assignedTo, workers) }}
                            title={getWorkerName(selectedJob.assignedTo, workers, partners)}
                          >
                            {getWorkerInitials(selectedJob.assignedTo, workers, partners)}
                          </div>
                          <span>{getWorkerName(selectedJob.assignedTo, workers, partners)}</span>
                        </div>
                      </div>
                    )}
                    {/* Timer Information - Only show active timers or accumulated time when paused */}
                    {(selectedJob.travelStartTime || selectedJob.workStartTime || selectedJob.accumulatedTravelTime > 0 || selectedJob.accumulatedWorkTime > 0) && (
                      <div className="mt-3 pt-3 border-t border-gray-700">
                        <p className="text-xs text-gray-400 mb-2">Tajmeri:</p>
                        {/* Show travel timer only if it's active OR if there's accumulated time but timer is paused */}
                        {((selectedJob.travelStartTime && selectedJob.status === 'On Road') || (selectedJob.accumulatedTravelTime > 0 && !selectedJob.travelStartTime)) && (
                          <div className="flex gap-2 text-xs">
                            <span className="text-gray-400 min-w-[100px]">Putovanje:</span>
                            <span className="text-blue-400">
                              {(() => {
                                const startTime = selectedJob.travelStartTime ? new Date(selectedJob.travelStartTime) : null;
                                const now = new Date();
                                const accumulated = selectedJob.accumulatedTravelTime || 0;
                                
                                // Check if timer is still active (travelStartTime exists and status is 'On Road')
                                const isTimerActive = selectedJob.status === 'On Road' && 
                                                     selectedJob.travelStartTime !== null && 
                                                     selectedJob.travelStartTime !== undefined &&
                                                     startTime !== null;
                                
                                let totalElapsed;
                                if (isTimerActive && startTime) {
                                  // Timer is active - calculate current elapsed + accumulated
                                  const currentElapsed = Math.floor((now - startTime) / 1000);
                                  totalElapsed = accumulated + currentElapsed;
                                } else {
                                  // Timer is paused - use only accumulated time (static)
                                  totalElapsed = accumulated;
                                }
                                
                                const hours = Math.floor(totalElapsed / 3600);
                                const minutes = Math.floor((totalElapsed % 3600) / 60);
                                const seconds = totalElapsed % 60;
                                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                              })()}
                            </span>
                            {selectedJob.travelStartTime && (
                              <span className="text-gray-500 text-xs">
                                (Start: {new Date(selectedJob.travelStartTime).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })})
                              </span>
                            )}
                          </div>
                        )}
                        {/* Show work timer only if it's active OR if there's accumulated time but timer is paused */}
                        {((selectedJob.workStartTime && (selectedJob.status === 'In Repair' || selectedJob.status === 'In Progress')) || (selectedJob.accumulatedWorkTime > 0 && !selectedJob.workStartTime)) && (
                          <div className="flex gap-2 text-xs mt-1">
                            <span className="text-gray-400 min-w-[100px]">Rad:</span>
                            <span className="text-purple-400">
                              {(() => {
                                const startTime = selectedJob.workStartTime ? new Date(selectedJob.workStartTime) : null;
                                const now = new Date();
                                const accumulated = selectedJob.accumulatedWorkTime || 0;
                                
                                // Check if timer is still active (workStartTime exists and status matches)
                                const isTimerActive = (selectedJob.status === 'In Repair' || selectedJob.status === 'In Progress') && 
                                                     selectedJob.workStartTime !== null && 
                                                     selectedJob.workStartTime !== undefined &&
                                                     startTime !== null;
                                
                                let totalElapsed;
                                if (isTimerActive && startTime) {
                                  // Timer is active - calculate current elapsed + accumulated
                                  const currentElapsed = Math.floor((now - startTime) / 1000);
                                  totalElapsed = accumulated + currentElapsed;
                                } else {
                                  // Timer is paused - use only accumulated time (static)
                                  totalElapsed = accumulated;
                                }
                                
                                const hours = Math.floor(totalElapsed / 3600);
                                const minutes = Math.floor((totalElapsed % 3600) / 60);
                                const seconds = totalElapsed % 60;
                                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                              })()}
                            </span>
                            {selectedJob.workStartTime && (
                              <span className="text-gray-500 text-xs">
                                (Start: {new Date(selectedJob.workStartTime).toLocaleTimeString('sr-RS', { hour: '2-digit', minute: '2-digit' })})
                              </span>
                            )}
                          </div>
                        )}
                        {/* Show accumulated time only if timers are paused (no active startTime) */}
                        {!selectedJob.travelStartTime && !selectedJob.workStartTime && (selectedJob.accumulatedTravelTime > 0 || selectedJob.accumulatedWorkTime > 0) && (
                          <div className="text-xs text-gray-500">
                            {selectedJob.accumulatedTravelTime > 0 && `Putovanje: ${Math.floor(selectedJob.accumulatedTravelTime / 60)} min (pauzirano)`}
                            {selectedJob.accumulatedTravelTime > 0 && selectedJob.accumulatedWorkTime > 0 && ' | '}
                            {selectedJob.accumulatedWorkTime > 0 && `Rad: ${Math.floor(selectedJob.accumulatedWorkTime / 60)} min (pauzirano)`}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>


              {/* Issue Description */}
              {selectedJob.issueDescription && (
                <div className="rounded-md border border-gray-700 bg-gray-800">
                  <button
                    type="button"
                    onClick={() => toggleJobDetailsSection('issueDescription')}
                    className="flex w-full items-center justify-between px-3 py-2 text-left"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-gray-100">
                      <FileText className="h-4 w-4" />
                      {t('jobs.issueDescription')}
                    </span>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform transform ${jobDetailsOpenSections.issueDescription ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {jobDetailsOpenSections.issueDescription && (
                    <div className="border-t border-gray-700 px-4 py-3 text-sm text-gray-300 space-y-2">
                      {(selectedJob.deviceType || selectedJob.deviceBrand || selectedJob.deviceModel || selectedJob.deviceSerialNumber) && (
                        <>
                          <p className="text-gray-400 font-semibold">Podaci o aparatu:</p>
                          {selectedJob.deviceType && (
                            <div className="flex gap-2">
                              <span className="text-gray-400">{t('jobs.deviceType')}:</span>
                              <span className="text-white">{selectedJob.deviceType}</span>
                            </div>
                          )}
                          {selectedJob.deviceBrand && (
                            <div className="flex gap-2">
                              <span className="text-gray-400">{t('jobs.deviceBrand')}:</span>
                              <span className="text-white">{selectedJob.deviceBrand}</span>
                            </div>
                          )}
                          {selectedJob.deviceModel && (
                            <div className="flex gap-2">
                              <span className="text-gray-400">{t('jobs.deviceModel')}:</span>
                              <span className="text-white">{selectedJob.deviceModel}</span>
                            </div>
                          )}
                          {selectedJob.deviceSerialNumber && formatDisplayValue(selectedJob.deviceSerialNumber) !== '-' && (
                            <div className="flex gap-2">
                              <span className="text-gray-400">{t('jobs.deviceSerialNumber')}:</span>
                              <span className="text-white">{formatDisplayValue(selectedJob.deviceSerialNumber)}</span>
                            </div>
                          )}
                        </>
                      )}
                      {Array.isArray(selectedJob.deviceImages) && selectedJob.deviceImages.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setShowDeviceImagesModal(true)}
                          className="mt-2 text-xs text-blue-400 hover:text-blue-300 underline"
                        >
                          Vidi slike ({selectedJob.deviceImages.length})
                        </button>
                      ) : (
                        <p className="mt-2 text-xs text-gray-500 italic">
                          Nema priloženih slika.
                        </p>
                      )}
                      <div>
                        <p className="text-gray-400 font-semibold mb-1">Opis problema:</p>
                        <p className="text-white whitespace-pre-wrap">{selectedJob.issueDescription}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Inventory materials for job */}
              <div className="rounded-md border border-gray-700 bg-gray-800">
                <button
                  type="button"
                  onClick={() => toggleJobDetailsSection('inventoryMaterials')}
                  className="flex w-full items-center justify-between px-3 py-2 text-left"
                >
                  <span className="flex items-center gap-2 text-sm font-medium text-gray-100">
                    <FileText className="h-4 w-4" />
                    {t('inventory.withdrawn.jobMaterialsAll', 'Povučena roba za posao')}
                  </span>
                  <svg
                    className={`h-4 w-4 text-gray-400 transition-transform transform ${jobDetailsOpenSections.inventoryMaterials ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {jobDetailsOpenSections.inventoryMaterials && (
                  <div className="border-t border-gray-700 px-4 py-3 space-y-3">
                    <WithdrawnItemsReminderBanner
                      jobId={selectedJob._id}
                      variant={effectiveUserType === 'company' ? 'admin' : 'worker'}
                    />
                    <JobMaterialsPanel
                      jobId={selectedJob._id}
                      showActions={effectiveUserType === 'company'}
                    />
                  </div>
                )}
              </div>

              {/* Worker status / timers – za radnika kada posao nije završen; za obe uloge kada je Finished (štampa + arhiva) */}
              {((effectiveUserType === 'worker' && selectedJob?.status !== 'Finished' && selectedJob?.status !== 'Completed' && selectedJob?.status !== 'Archived') ||
                (selectedJob?.status === 'Finished' && (effectiveUserType === 'worker' || effectiveUserType === 'company'))) && (
                <div className="rounded-md border border-gray-700 bg-gray-800">
                  <button
                    type="button"
                    onClick={() => toggleJobDetailsSection('statusControls')}
                    className="flex w-full items-center justify-between px-3 py-2 text-left"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-gray-100">
                      <Activity className="h-4 w-4" />
                      {selectedJob?.status === 'Finished' ? 'Radni nalog i arhiva' : 'Upravljanje poslom'}
                    </span>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform transform ${jobDetailsOpenSections.statusControls ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {jobDetailsOpenSections.statusControls && (
                    <div className="border-t border-gray-700 px-4 py-3 space-y-3">
                      {selectedJob?.status !== 'Finished' && selectedJob?.status !== 'Completed' && (
                        <div className="text-xs text-gray-400">
                          Status posla:{' '}
                          <span className="text-gray-200 font-medium">
                            {getWorkerLocationLabel(selectedJob)}
                          </span>
                        </div>
                      )}
                      <JobStatusControls 
                        job={selectedJob} 
                        user={effectiveUser || user}
                        globalUseAI={typeof window !== 'undefined' ? localStorage.getItem('worker_global_useAI') === 'true' : false}
                        globalEnableParking={typeof window !== 'undefined' ? localStorage.getItem('worker_global_enableParking') === 'true' : false}
                        onStatusUpdate={(newStatus) => {
                          if (newStatus === 'Completed' || newStatus === 'Finished' || newStatus === 'Archived') {
                            dispatch(refreshJobs());
                            setSelectedJob(null);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Print / Edit / Delete — print je uvek u istom redu, pre izmene i brisanja */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-600">
                <button
                  type="button"
                  onClick={() => setIsPrintModalOpen(true)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  {t('jobs.printWorkOrder', 'Štampaj')}
                </button>
                {canEditJobs && (
                  <button
                    onClick={() => {
                      setIsPrintModalOpen(false);
                      setEditingJob(selectedJob);
                      setSelectedJob(null);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    {t('jobs.editJob')}
                  </button>
                )}
                {canDeleteJobs && (
                  <button
                    onClick={() => handleDeleteJob(selectedJob)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('jobs.deleteJob')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isPrintModalOpen && selectedJob && (
        <WorkOrderPrintModal
          job={selectedJob}
          onClose={() => setIsPrintModalOpen(false)}
        />
      )}

      {showDeviceImagesModal && selectedJob && Array.isArray(selectedJob.deviceImages) && selectedJob.deviceImages.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg border border-gray-700 max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-700 px-4 py-2">
              <h3 className="text-sm font-semibold text-white">
                Slike aparata – {formatDisplayValue(selectedJob.deviceType) || 'nedefinisano'}
              </h3>
              <button
                type="button"
                onClick={() => setShowDeviceImagesModal(false)}
                className="text-gray-400 hover:text-white text-sm"
              >
                Zatvori
              </button>
            </div>
            <div className="p-4 overflow-auto">
              <div className="flex flex-wrap gap-3 justify-start">
                {selectedJob.deviceImages.map((imgUrl, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => window.open(imgUrl, '_blank')}
                    className="relative border border-gray-700 rounded-md overflow-hidden bg-black/40 w-20 h-20 sm:w-24 sm:h-24"
                  >
                    <img
                      src={imgUrl}
                      alt={`Aparat ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-gray-800 pb-4 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">{t('jobs.editJob')}</h2>
              <button
                onClick={() => setEditingJob(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <JobForm
              isEdit={true}
              jobData={editingJob}
              onClose={() => {
                setEditingJob(null);
                setSelectedJob(null); // Close the job details modal as well
                setEditingJobId(null); // Close inline editing box
                setEditingJobDateTime({ date: '', time: '' }); // Reset inline editing state
                dispatch(refreshJobs());
              }}
            />
          </div>
        </div>
      )}

      {/* Job Form Modal */}
      {showJobForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            <button
              onClick={() => setShowJobForm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
            <JobForm
              onClose={() => setShowJobForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarWithDragDrop;
